/**
 * Query keys (00-contracts §14.4).
 *
 * Never inline an array literal as a key — always `qk.*`. Order is
 * `['tvitvi', scope, ...scopeId, domain, kind, ...discriminators]`. Every org-scoped key starts
 * with `['tvitvi','org',orgId]` so switching organization or signing out wipes by prefix and
 * cross-org cache bleed is structurally impossible.
 */
import type {
  ContactCompanySortKey,
  ContactSortKey,
  GuestSortKey,
  ContactSuggestionField,
  OrganizationMemberSortKey,
  OrganizationMemberStatus,
  OrgRole,
  PerformanceSortKey,
  PerformanceStatus,
  SortDirection,
  TaskPriority,
  TaskSortKey,
  TaskStatus,
} from '@tvitvi/shared';

/**
 * The contacts list is addressed by what narrows it AND by what orders it, never by a stringified
 * URL. The sort belongs in the key for the same reason the search does: two orders are two
 * different answers, and a cache that could not tell them apart would show the first one under
 * the second one's heading — and would carry a keyset cursor across a sort the API mints cursors
 * per (`decodeCursor` refuses one issued for another sort).
 */
export interface ContactListFilters {
  search?: string | undefined;
  tagIds?: readonly string[] | undefined;
  /** «Hvem jobber i dette firmaet» — the firm page's own list of people (migrering 0088). */
  companyId?: string | undefined;
  /** «Vis meg agentene» — ONE type, because a contact has exactly one (vedtak 57.1). */
  sort?: ContactSortKey | undefined;
  direction?: SortDirection | undefined;
  /** A2: den sammenslåtte lista i Kontakter (org-delte, mine private, mine koblinger, kollegene). */
  everyone?: boolean | undefined;
}

/** A7: hva personvelgeren spør om. */
export interface PersonSearchParams {
  q: string;
  scope: string;
  onlyScope?: boolean | undefined;
  membersOnly?: boolean | undefined;
}

/** What narrows and orders the firm register. The same shape, one field narrower. */
export interface CompanyListFilters {
  search?: string | undefined;
  sort?: ContactCompanySortKey | undefined;
  direction?: SortDirection | undefined;
}

export interface MemberListFilters {
  search?: string | undefined;
  sort?: OrganizationMemberSortKey | undefined;
  direction?: SortDirection | undefined;
  /**
   * «Aktive» or «Deaktiverte» (QA15 blokk B2). Absent means active, the same way absent means
   * every other default here — and it is IN the key, because the two are two different answers
   * and a shared key would serve one under the other's heading.
   */
  status?: OrganizationMemberStatus | undefined;
  /** F53: bare én rolle. I nøkkelen av samme grunn som `status`. */
  role?: OrgRole | undefined;
  /** 253.69: bare plattformadminene (Tvitvi-organisasjonen). I nøkkelen av samme grunn. */
  platformAdmins?: boolean | undefined;
}

/** What narrows and orders a task list. The grouping IS the sort — see `tasks/search-params.ts`. */
export interface TaskListFilters {
  assigneePersonId?: string | undefined;
  statuses?: readonly TaskStatus[] | undefined;
  /**
   * The three `PAGES.md` asks for beside status: «prioritet, avdeling» and «forfalt».
   *
   * In the KEY, like every other narrowing here, for the reason the deadline window gives below:
   * two filters are two different answers, and one cache entry serving both would show the urgent
   * list under the "alle prioriteter" heading. `departmentId` sits beside `editionId` rather than
   * inside it — a group's work crosses years, and a year's work crosses groups.
   */
  priorities?: readonly TaskPriority[] | undefined;
  departmentId?: string | undefined;
  overdue?: boolean | undefined;
  /** Fritekstsøket (0107). I NØKKELEN, som resten: to søk er to lister. */
  search?: string | undefined;
  sort?: TaskSortKey | undefined;
  direction?: SortDirection | undefined;
  /**
   * THE DEADLINE WINDOW, WHICH IS WHAT MAKES THE CALENDAR'S DAY DETAIL AN ORDINARY TASK LIST.
   *
   * Both bounds are inclusive calendar days, so one day is `dueFrom === dueTo`
   * (`listTasksQuerySchema`). They are in the KEY rather than beside it because two windows are
   * two different answers: the fourteenth of October and the fifteenth must never be served out
   * of one cache entry, and clicking along a week would otherwise show the first day's tasks
   * under every later day's heading.
   */
  dueFrom?: string | undefined;
  dueTo?: string | undefined;
  /**
   * WHICH ÅRGANG'S TASKS — the one filter on this list that is not a filter at all.
   *
   * `/tasks` never sends it and asks the organisation's whole question.
   * `/festivals/:f/editions/:e/tasks` always sends it, because the year is in the PATH rather
   * than in a chip: it is not something that page might stop asking, it is which list it IS. It
   * still belongs in the key for the ordinary reason every discriminator here does — two years
   * are two different answers, and one cache entry for the pair would put Nordlys 2027's work
   * under Nordlys 2026's heading.
   */
  editionId?: string | undefined;
  /**
   * HVILKE ARRANGEMENTER LESEREN HAR KRYSSET AV (#39), ved siden av `editionId` og aldri sammen
   * med den: det ene er årgangens egen liste, det andre er utvalget på organisasjonens.
   *
   * I NØKKELEN av samme grunn som året over: to utvalg er to forskjellige svar, og én
   * cache-oppføring for begge ville vist den ene festivalens arbeid under den andres overskrift.
   */
  editionIds?: readonly string[] | undefined;
  /**
   * WHICH BOOKING’S TASKS (PERFORMANCE.md, fane 3), on the same terms as the year above it.
   *
   * The act's own tab always sends it and no other page ever does. In the key for the ordinary
   * reason: two bookings are two different answers, and one entry for the pair would put one
   * act's work under another act's heading.
   */
  performanceId?: string | undefined;
  /**
   * SPILLESTEDETS ARRANGEMENT OG HELE HUSET (sak 229, V4). Fanen Oppgaver sender det første,
   * husets oversikt det andre (alle årgangene, som huset aldri ser). I nøkkelen som resten.
   */
  venueNightId?: string | undefined;
  festivalId?: string | undefined;
  /**
   * KOI Del 1 — etikettfilteret, «Min dag» og «Jeg følger».
   *
   * All three in the key for the reason every narrowing here is: two filters are two answers, and
   * one entry serving both would show the labelled list under the "alle etiketter" heading.
   *
   * `myDay` IS A DATE and not a boolean, because `task_my_day` carries the day: «hva sto på dagen
   * min i går» is the same request with another value, and a `true` would have to invent which day
   * it meant, in which zone. The client knows the day it is drawing; it says so.
   */
  labelId?: string | undefined;
  myDay?: string | undefined;
  watching?: boolean | undefined;
  /** Oppgavene på én booking (sak 216.9): `task_links` av typen og id-en. Begge eller ingen. */
  linkType?: 'agency_booking' | 'booking_enquiry' | undefined;
  linkId?: string | undefined;
}

/**
 * Tidslinjas vindu og dens fire innsnevringer (KOI Del 1).
 *
 * Et eget filtersett og ikke `TaskListFilters` med to felt til: tidslinja har ingen markør, ingen
 * sortering å velge og ingen sidestørrelse, og et felles sett ville båret fire felt som ikke betyr
 * noe i den ene enden og tre som ikke betyr noe i den andre.
 */
export interface TaskTimelineFilters {
  from: string;
  to: string;
  editionId?: string | undefined;
  departmentId?: string | undefined;
  assigneePersonId?: string | undefined;
  statuses?: readonly TaskStatus[] | undefined;
  /* Resten av listas filtre (K6 M3): tidslinja så bort fra dem og viste hele huset. */
  editionIds?: readonly string[] | undefined;
  priorities?: readonly TaskPriority[] | undefined;
  overdue?: boolean | undefined;
  search?: string | undefined;
  labelId?: string | undefined;
  myDay?: string | undefined;
}

/**
 * The month grid's window: the 42 days the reader can see, inclusive.
 *
 * `editionId` USED TO BE ABSENT HERE, under a note ending "the day it filters, the filter joins
 * the key". That day arrived with the årgang's own calendar. `/calendar` still never sends one
 * and still asks the organisation's whole question — the totalkalender is the counterpart to the
 * organisation's task list — and `/festivals/:f/editions/:e/calendar` always sends one, because
 * the year is in the PATH rather than in a control.
 *
 * IT IS IN THE KEY BECAUSE IT CHANGES THE ANSWER, not because it narrows it. October counted for
 * one årgang and October counted for the whole organisation are two different sets of numbers
 * over the same 42 days, and one cache entry for the pair would draw a year's private count under
 * the organisation's heading — or, worse, the organisation's total under the year's.
 */
export interface CalendarWindow {
  from: string;
  to: string;
  editionId?: string | undefined;
}

/**
 * The inbox offers one order and five filters.
 *
 * `unread`, `dm`, `closed` og `q` er innboksens egne faner og søkefelt (KOI Del 4). `linkType` /
 * `linkId` is a different question asked of the same endpoint — "samtaler om denne kontakten" on a
 * contact's page (PAGES.md, Contacts [F1]) — and the pair is in the KEY for the ordinary reason
 * every discriminator here is: two contacts' threads are two answers, and one cache entry for the
 * pair would show the first contact's conversations under the second one's name. The API refuses
 * one half without the other, so they travel together.
 *
 * ALLE FEM ER I NØKKELEN, av nøyaktig den samme grunnen: «uleste» og «arkiverte» er to lister, og
 * ett hurtigbufferoppslag for begge ville vist den ene under den andres fane.
 */
export interface ThreadListFilters {
  unread?: boolean | undefined;
  dm?: boolean | undefined;
  closed?: boolean | undefined;
  /** F48: bare trådene leseren har dempet. */
  muted?: boolean | undefined;
  /** 0931 (84): «Bookinger i organisasjonen» — tilgang uten å følge. */
  bookings?: boolean | undefined;
  q?: string | undefined;
  linkType?: 'contact' | 'festival' | 'edition' | undefined;
  linkId?: string | undefined;
}

/**
 * What narrows and orders ONE edition's line-up.
 *
 * NO `editionId` HERE, and that is the same call `editionList` made one level up: the edition is
 * not a filter this list might drop, it is which line-up this IS, so it sits in the key as a
 * discriminator of its own rather than inside an object that could arrive empty. Two årganger are
 * two different answers and must never come out of one cache entry.
 *
 * The sort IS in the key, unlike `editionList`, because there are three of them
 * (`PERFORMANCE_SORT_KEYS`) and a keyset cursor is minted per sort spec — carrying one across a
 * change of order is a 400 rather than a wrong page.
 */
export interface PerformanceListFilters {
  stageId?: string | undefined;
  statuses?: readonly PerformanceStatus[] | undefined;
  /** Fritekstsøket (0107). Med i NØKKELEN: to søk er to lister, ikke én med to svar. */
  search?: string | undefined;
  sort?: PerformanceSortKey | undefined;
  direction?: SortDirection | undefined;
}

/**
 * What narrows ONE booking's guest list. One field, because there is one filter.
 *
 * NO SORT, unlike every other list filter here. `GUEST_SORT_KEYS` has exactly one member and the
 * contract says why — "a guest list is read at a door by somebody looking for a name, so it is
 * ordered by name and nothing else" — so there is one order and nothing to tell two cache entries
 * apart by. `editionList` omits its sort for the same reason.
 *
 * NO `performanceId` EITHER: it is not something the list might stop sending, it is WHICH list
 * this is, so it sits in the key as a discriminator of its own rather than inside an object that
 * could arrive empty. `performanceList` wraps its edition id the same way.
 *
 * `arrived` IS IN THE KEY BECAUSE IT CHANGES THE ANSWER. The door works from the outstanding half
 * and the office reads the whole list; one cache entry for the pair would show the filtered page
 * under the unfiltered heading the moment somebody cleared the chip.
 */
export interface GuestListFilters {
  arrived?: boolean | undefined;
  /**
   * WHICH ORDER, in the key like every other discriminator here (Atle, 2. september: «alle
   * tabeller og slikt kan sorteres»).
   *
   * It used to be a constant sent by the query function, under a note saying the door reads by
   * name and nothing else. The door does; the office does not, and a keyset cursor is minted per
   * sort spec — so two orders are two different answers and must never share a cache entry.
   */
  sort?: GuestSortKey | undefined;
  direction?: SortDirection | undefined;
}

export const qk = {
  all: ['tvitvi'] as const,
  global: () => [...qk.all, 'global'] as const,
  me: () => [...qk.global(), 'me'] as const,
  /** «Min profil» (#7) — global, fordi mennesket er det samme i hvert hus det står i. */
  myProfile: () => [...qk.global(), 'my-profile'] as const,
  /** Koblinger til meg (#8) — global, fordi forespørselen kommer fra et annet hus. */
  personLinks: () => [...qk.global(), 'person-links'] as const,
  /** Kontaktforespørslene til meg (253.16) — global av samme grunn: de kommer fra et annet hus. */
  connectionRequests: () => [...qk.global(), 'connection-requests'] as const,
  /**
   * Samtalen om én kontaktforespørsel, sett fra meg (0970, 259.11) — under forespørslene, så et svar
   * (som ugyldiggjør dem) også friskar kortet i samtalen.
   */
  contactRequestConversation: (threadId: string) =>
    [...qk.connectionRequests(), 'conversation', threadId] as const,
  /** Konsertene mine (sak 216) — global, fordi de gjelder mennesket på tvers av band og byråer. */
  myConcerts: () => [...qk.global(), 'concerts'] as const,
  /** Det personen tar med seg (0384): instrument, bosted, kosthold, allergier. Menneskets, ikke husets. */
  myDetails: () => [...qk.global(), 'my-details'] as const,
  /** «Steinkjer festival har registrert: Laktose. Stemmer det?» (253.12) — under profilen, så de friskes sammen. */
  myRegisteredFacts: () => [...qk.myDetails(), 'registered'] as const,
  myConcert: (kind: string, id: string) => [...qk.global(), 'concerts', kind, id] as const,
  /** Kontrakt og økonomi (0453) — under konserten, så det som frisker opp konserten tar dem med. */
  myConcertDeal: (kind: string, id: string) => [...qk.myConcert(kind, id), 'deal'] as const,
  /** Oppgavene gitt meg fra hus jeg ikke er med i (sak 216.9, 0395) — menneskets, ikke husets. */
  myGivenTasks: () => [...qk.global(), 'given-tasks'] as const,
  /** Det som er delt med meg (sak 232, T4) — menneskets, på tvers av organisasjonene som delte. */
  myShares: () => [...qk.global(), 'shares'] as const,
  /** F12, F13: det som er delt av dokumenter, mapper og kart — under delingene, så de friskes sammen. */
  mySharedContent: (grantId: string) => [...qk.myShares(), grantId, 'content'] as const,
  /** F18: salen eller arrangementet slik mottakeren leser det — under lista, så den feier med. */
  mySharedView: (grantId: string) => [...qk.myShares(), 'view', grantId] as const,
  /** A9: delte artister i sidemenyen — under delingene, så et svar på en deling feier med. */
  mySharedArtists: () => [...qk.myShares(), 'artists'] as const,
  /** §5.1: Delte produksjoner i sidemenyen — under delingene, så et svar eller en ny deling feier med. */
  mySharedProductions: () => [...qk.myShares(), 'productions'] as const,
  /**
   * Kolonnevalget per tabell (ordren Del 3) — GLOBALT, ikke under `qk.org`.
   *
   * Raden er scopet per MENNESKE og ikke per hus: den samme agenten skal lese registeret med de
   * samme kolonnene i begge husene sine. Under org-prefikset ville hvert husbytte tømt den og
   * hentet den samme raden på nytt.
   */
  tablePrefs: (tableKey: string) => [...qk.global(), 'table-prefs', tableKey] as const,
  /**
   * Visningsvalget (QA13 runde 2, K-18). GLOBALT som kolonnevalget over, og av samme grunn: raden
   * er menneskets og ruta er `@OrgScope('none')`. Huset ligger i NØKKELEN — `calendar:<org-id>` —
   * så to hus har to nøkler uten å ligge under hvert sitt org-prefiks, og et husbytte tømmer
   * derfor ikke den ene for å hente den andre.
   */
  viewPrefs: (viewKey: string) => [...qk.global(), 'view-prefs', viewKey] as const,
  /** Varslingsvalgene (F51). GLOBALT som de to over: raden er menneskets, i alle husene. */
  notificationPreferences: () => [...qk.global(), 'notification-preferences'] as const,
  /**
   * B5: mine AI-tilkoblinger og godkjenningssidens forespørsel. GLOBALE: tilkoblingen er brukerens
   * på tvers av organisasjonene (`@OrgScope('none')`), og forespørselen kommer før noe hus er valgt.
   */
  aiConnections: () => [...qk.global(), 'ai-connections'] as const,
  oauthConsentRequest: (requestId: string) => [...qk.global(), 'oauth-request', requestId] as const,
  /** B4: organisasjonens AI-brytere og tilkoblinger — under huset, og under ett prefiks de feies med. */
  aiOrganization: (orgId: string) => [...qk.org(orgId), 'ai'] as const,
  aiOrganizationSettings: (orgId: string) => [...qk.aiOrganization(orgId), 'settings'] as const,
  aiOrganizationConnections: (orgId: string) =>
    [...qk.aiOrganization(orgId), 'connections'] as const,
  /** B4: antall AI-tilkoblinger per organisasjon i Control Room — plattformens, globale. */
  controlRoomAiConnections: () => [...qk.global(), 'platform', 'ai-connections'] as const,
  organizations: () => [...qk.global(), 'organizations'] as const,
  /** Huset man står i, med alle feltene (#9). Under org-en, ikke under lista — se queries/organization.ts. */
  organizationDetail: (orgId: string) => [...qk.org(orgId), 'organization'] as const,
  /**
   * Forespørslene om å bli koblet til huset man står i (9a).
   *
   * Under org-en og ikke global, motsatt av `qk.personLinks()`: en kobling til et MENNESKE følger
   * mennesket mellom husene, mens denne er rettet mot ett bestemt hus og bare synlig der.
   */
  organizationCompanyLinks: (orgId: string) =>
    [...qk.org(orgId), 'organization', 'company-links'] as const,
  /**
   * THE ONE ORG-SCOPED THING THAT IS NOT ORG-SCOPED, and it has to be.
   *
   * The invitation preview runs with no session and no organisation — see the query module. There
   * is no org id to key it under, so it lives beside `terms` under the global prefix, keyed by
   * the token that IS the whole question.
   */
  organizationLinkPreview: (token: string) =>
    [...qk.global(), 'organization-link-preview', token] as const,

  /**
   * Og brukerinvitasjonens forhåndsvisning, av nøyaktig samme grunn: den som klikker lenken har
   * ingen sesjon og ingen organisasjon ennå. Tokenet ER hele spørsmålet, så det er hele nøkkelen.
   */
  invitationPreview: (token: string) => [...qk.global(), 'invitation-preview', token] as const,

  /** Platform-level legal documents. Immutable tier: a terms version never changes in place. */
  terms: (locale: string) => [...qk.global(), 'terms', locale] as const,
  /** Teksten til én versjon (QA13 punkt 9). Like uforanderlig som raden den hører til. */
  legalDocument: (kind: string, version: string, locale: string) =>
    [...qk.global(), 'legalDocument', kind, version, locale] as const,
  /**
   * Gründerens valg av valg (GRUNDER.md del B) — GLOBALT: det gjelder alle organisasjonene, og
   * ruta er `@OrgScope('none')`. Et husbytte skal verken tømme den eller hente den på nytt.
   */
  platformPermissionOptions: () => [...qk.global(), 'platform', 'permission-options'] as const,
  /**
   * Tvitvis standarder (G4, 0942): scenetider, personfelt og ressursmaler. Plattformens, globale som
   * over. Malene typebyggeren tilbyr er de samme radene sett fra et hus, og lever under samme prefiks,
   * så en lagring i Control Room feier begge.
   */
  controlRoomDefaults: () => [...qk.global(), 'platform', 'defaults'] as const,
  /** Control Room › Vilkår (1329): de fire dokumentene og revisjonene deres. */
  controlRoomLegal: () => [...qk.global(), 'platform', 'legal'] as const,
  resourceTypePresets: () => [...qk.controlRoomDefaults(), 'resource-type-presets'] as const,
  /** Control Room › Spillesteder (QA15-S E, 1260). Én prefiks: en kobling endrer lista, stedet og forespørslene. */
  controlRoomVenues: () => [...qk.global(), 'platform', 'venues'] as const,
  controlRoomVenueList: (filters: Record<string, unknown>) =>
    [...qk.controlRoomVenues(), 'list', filters] as const,
  controlRoomVenue: (id: string) => [...qk.controlRoomVenues(), 'detail', id] as const,
  controlRoomVenueRequests: () => [...qk.controlRoomVenues(), 'requests'] as const,
  controlRoomVenueSummary: () => [...qk.controlRoomVenues(), 'summary'] as const,
  controlRoomOrganizationVenues: (organizationId: string) =>
    [...qk.controlRoomVenues(), 'organization', organizationId] as const,
  /**
   * Control Room: Oversikt og Organisasjoner (0479) — globale av samme grunn: plattformens tall,
   * `@OrgScope('none')`. `controlRoomOrganizations()` er prefikset en frysing eller en ny
   * organisasjon friskes opp med (lista, sidene og brukerlistene på én gang).
   */
  controlRoomOverview: () => [...qk.global(), 'platform', 'overview'] as const,
  controlRoomOrganizations: () => [...qk.global(), 'platform', 'organizations'] as const,
  controlRoomOrganizationList: (filters: {
    search?: string;
    sort: string;
    direction: string;
    /* F58: «Arkiverte». */
    archived?: boolean;
  }) => [...qk.controlRoomOrganizations(), 'list', filters] as const,
  controlRoomOrganization: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'detail', organizationId] as const,
  controlRoomOrganizationUsers: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'users', organizationId] as const,
  /** 253.41: «Brukere uten organisasjon» — de personlige kontoene, ute av kundelista. */
  controlRoomPeopleWithoutOrganization: () =>
    [...qk.controlRoomOrganizations(), 'people-without-organization'] as const,
  /** Control Room: plattformadminer, Drift og Logg (0478) — plattformens, globale som over. */
  controlRoomAdmins: () => [...qk.global(), 'platform', 'admins'] as const,
  controlRoomOperations: () => [...qk.global(), 'platform', 'operations'] as const,
  /** Control Room › Leverandører (Atle 03.11): kvoter og forbruk, plattformens. */
  controlRoomVendors: () => [...qk.global(), 'platform', 'vendors'] as const,
  controlRoomActivity: () => [...qk.global(), 'platform', 'activity'] as const,
  /**
   * Control Room: byråskanningen (0482). `controlRoomScan()` er prefikset: «Kjør nå» og en lagret
   * tidsplan friskes opp sammen med loggen.
   */
  controlRoomScan: () => [...qk.global(), 'platform', 'scan'] as const,
  controlRoomScanSchedule: () => [...qk.controlRoomScan(), 'schedule'] as const,
  controlRoomScanRuns: () => [...qk.controlRoomScan(), 'runs'] as const,
  /** Byråene skanningen leser, med artistsiden (0840, sak 243). Søket hører til nøkkelen. */
  controlRoomScanAgencies: (q = '') => [...qk.controlRoomScan(), 'agencies', q] as const,
  /**
   * Control Room: den offisielle artistlista (sak 247). `controlRoomOfficial()` er prefikset: en
   * lagring publiseres til alle, så tallene, lista og «Må sjekkes» friskes opp sammen.
   */
  controlRoomOfficial: () => [...qk.global(), 'platform', 'official'] as const,
  /* Control Room › Soundcharts (sak 273). `controlRoomSoundcharts()` er prefikset hver endring feier. */
  controlRoomSoundcharts: () => [...qk.global(), 'platform', 'soundcharts'] as const,
  controlRoomSoundchartsOverview: () => [...qk.controlRoomSoundcharts(), 'overview'] as const,
  controlRoomSoundchartsArtists: (q = '') =>
    [...qk.controlRoomSoundcharts(), 'artists', q] as const,
  controlRoomSoundchartsSuggestions: () => [...qk.controlRoomSoundcharts(), 'suggestions'] as const,
  controlRoomSoundchartsErrors: () => [...qk.controlRoomSoundcharts(), 'errors'] as const,
  controlRoomSoundchartsTestRuns: () => [...qk.controlRoomSoundcharts(), 'test-runs'] as const,
  controlRoomOfficialSummary: () => [...qk.controlRoomOfficial(), 'summary'] as const,
  controlRoomOfficialArtists: (filters: {
    q?: string;
    agencyId?: string;
    sort: string;
    direction: string;
  }) => [...qk.controlRoomOfficial(), 'artists', filters] as const,
  controlRoomOfficialAgencies: () => [...qk.controlRoomOfficial(), 'agencies'] as const,
  controlRoomOfficialReview: () => [...qk.controlRoomOfficial(), 'review'] as const,
  /**
   * Control Room: opprette, fryse og åpne (0480). Invitasjonene er plattformens; frysing og
   * abonnement ligger under `controlRoomOrganizations()`, så en frysing friskes opp sammen med
   * lista og siden. «Nytt hus»-forhåndsvisningen har ingen sesjon, som `invitationPreview`.
   */
  controlRoomOrganizationInvitations: () =>
    [...qk.global(), 'platform', 'organization-invitations'] as const,
  controlRoomOrganizationAdmin: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'admin', organizationId] as const,
  /** F58: arkivet, under `controlRoomOrganizations()` så lista og siden friskes opp med det. */
  controlRoomOrganizationArchive: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'archive', organizationId] as const,
  /** Modulene per organisasjon (0483), under `controlRoomOrganizations()` som frysingen. */
  controlRoomOrganizationModules: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'modules', organizationId] as const,
  /** QA13 #5 (0940): byrået i den offisielle lista som organisasjonen ER. */
  controlRoomOrganizationOfficialAgency: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'official-agency', organizationId] as const,
  /** QA13 #8 (0941): planlagt sletting for én organisasjon, og alt som står i «Planlagt sletting». */
  controlRoomOrganizationDeletion: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'deletion', organizationId] as const,
  controlRoomDeletions: () => [...qk.controlRoomOrganizations(), 'deletions'] as const,
  /** QA13 #6: de små diagrammene på oversikten. */
  controlRoomOverviewSeries: () => [...qk.controlRoomOverview(), 'series'] as const,
  /** SMS (jobb 3, 0495): bryteren og telleren for hele plattformen, og telleren per organisasjon. */
  controlRoomSms: () => [...qk.global(), 'platform', 'sms'] as const,
  controlRoomOrganizationSmsUsage: (organizationId: string) =>
    [...qk.controlRoomOrganizations(), 'sms-usage', organizationId] as const,
  /** Om SMS er på — det dialogene spør før de tilbyr den. Global: svaret er plattformens. */
  messagingChannels: () => [...qk.global(), 'messaging-channels'] as const,
  organizationInvitationPreview: (token: string) =>
    [...qk.global(), 'organization-invitation-preview', token] as const,
  org: (orgId: string) => [...qk.all, 'org', orgId] as const,
  // --- Phase 1 insertion point: add domains below, same shape, never inline a key elsewhere ---

  /** Contacts (Phase 1, page 1). Filters travel as ONE plain object, always last. */
  contacts: (orgId: string) => [...qk.org(orgId), 'contacts'] as const,
  contactList: (orgId: string, filters: ContactListFilters) =>
    [...qk.contacts(orgId), 'list', filters] as const,
  contact: (orgId: string, contactId: string) =>
    [...qk.contacts(orgId), 'detail', contactId] as const,
  /** Kontaktkortet (17): hva personen gjør hos oss, og samtalene personen er med i. */
  contactInvolvement: (orgId: string, contactId: string) =>
    [...qk.contacts(orgId), 'detail', contactId, 'involvement'] as const,
  /** «Mine kontakter» (253.16). Under kontaktene: husets rad på personen står med i svaret. */
  myContacts: (orgId: string, search: string) => [...qk.contacts(orgId), 'mine', search] as const,
  /** Personkortet fra en tråd eller en sak (95). Huset man står i avgjør hva sakene viser. */
  personCardView: (orgId: string, personId: string) =>
    [...qk.contacts(orgId), 'person-card', personId] as const,
  /**
   * What the organization already calls a job and a company — one entry per field and prefix, so
   * typing another letter is a new key and backspacing is a cache hit rather than a round trip.
   */
  contactSuggestions: (orgId: string, field: ContactSuggestionField, prefix: string) =>
    [...qk.contacts(orgId), 'suggestions', field, prefix] as const,
  /** 234.1/F56: kontaktene én kontakt kan slås sammen med, per søk. */
  contactMergeCandidates: (orgId: string, contactId: string, search: string) =>
    [...qk.contacts(orgId), 'merge-candidates', contactId, search] as const,
  /** A7: den ene personvelgeren. Under kontaktene, så en ny eller endret kontakt friskes opp i den. */
  personSearch: (orgId: string, params: PersonSearchParams) =>
    [...qk.contacts(orgId), 'person-search', params] as const,

  /**
   * The firm register (vedtak 100.3). Its own domain rather than a branch of `contacts`, because
   * invalidating it must not throw away every contact list the reader has: renaming a firm touches
   * both, and the mutation says so explicitly rather than the key shape deciding it silently.
   */
  companies: (orgId: string) => [...qk.org(orgId), 'contact-companies'] as const,
  companyList: (orgId: string, filters: CompanyListFilters) =>
    [...qk.companies(orgId), 'list', filters] as const,
  company: (orgId: string, companyId: string) =>
    [...qk.companies(orgId), 'detail', companyId] as const,
  /** Historikken mellom to hus, per ÅR: to år er to svar (vedtak 97/99). */
  companyHistory: (orgId: string, companyId: string, year: number) =>
    [...qk.company(orgId, companyId), 'history', year] as const,

  /**
   * Byråkoblingene (vedtak 93 og 99). Org-scoped like everything else here — a link is one
   * house's own row, and the counterparty's copy lives in their tenant where RLS keeps it.
   */
  organizationLinks: (orgId: string) => [...qk.org(orgId), 'organization-links'] as const,
  organizationLinkList: (orgId: string) => [...qk.organizationLinks(orgId), 'list'] as const,
  /** «Organisasjoner vi samarbeider med» (sak 239.21): lista og velgerens søk. */
  organizationPartners: (orgId: string) => [...qk.org(orgId), 'organization-partners'] as const,
  organizationPartnerList: (orgId: string) => [...qk.organizationPartners(orgId), 'list'] as const,
  organizationPartnerCandidates: (orgId: string, search: string) =>
    [...qk.organizationPartners(orgId), 'candidates', search] as const,

  /** Org-defined labels. Reference tier: they change rarely and every list filters by them. */
  tags: (orgId: string) => [...qk.org(orgId), 'tags'] as const,
  tagList: (orgId: string) => [...qk.tags(orgId), 'list'] as const,

  /** The Organization tab: the same people, filtered by membership. */
  members: (orgId: string) => [...qk.org(orgId), 'members'] as const,
  memberList: (orgId: string, filters: MemberListFilters) =>
    [...qk.members(orgId), 'list', filters] as const,

  /**
   * De som er invitert og ikke har svart (Del 4.4).
   *
   * EGEN GREN OG IKKE EN UNDER `members`, fordi de to er motsetningen av hverandre: en invitasjon
   * forsvinner fra denne lista i det øyeblikket den blir et medlemskap. Å invalidere den ene skal
   * invalidere den andre — og mutasjonene sier det høyt framfor at nøkkelformen avgjør det stille.
   */
  invitations: (orgId: string) => [...qk.org(orgId), 'invitations'] as const,
  invitationList: (orgId: string) => [...qk.invitations(orgId), 'list'] as const,
  /** «Har denne adressen allerede en konto?» — ett boolsk svar per adresse (sak 194). */
  invitationAccount: (orgId: string, email: string) =>
    [...qk.invitations(orgId), 'account', email.trim().toLowerCase()] as const,

  /**
   * Tasks (Phase 1, page 2). The sort is in the key because the grouping IS the sort: two
   * groupings are two different answers, and a keyset cursor is minted per sort spec — carrying
   * one across would be a 400 rather than a wrong page.
   */
  tasks: (orgId: string) => [...qk.org(orgId), 'tasks'] as const,
  taskList: (orgId: string, filters: TaskListFilters) =>
    [...qk.tasks(orgId), 'list', filters] as const,
  task: (orgId: string, taskId: string) => [...qk.tasks(orgId), 'detail', taskId] as const,
  /**
   * One task's comments (PAGES.md, Tasks [F1]).
   *
   * UNDER THE TASK'S OWN DETAIL KEY, so a write to the task sweeps its comments with it and a
   * comment does not have to know about the task's other invalidations. That is the same nesting
   * `threadMessages` has under `thread`, and for the same reason — except that a task's
   * co-assignees and dependencies are NOT here: those are fields of `TaskDto`, so the detail key
   * already addresses them.
   */
  taskComments: (orgId: string, taskId: string) => [...qk.task(orgId, taskId), 'comments'] as const,
  /**
   * KOI Del 1. Two of the three sit UNDER the task's own detail key, for the reason the comments
   * do: a write to the task sweeps them, and neither has to know about the task's other
   * invalidations. The checklist is there even though it also arrives as a field of `TaskDto` —
   * the panel re-reads it after every tick, and a list of seven does not deserve a whole task.
   *
   * `taskLabels` is NOT under it, and could not be: an etikett belongs to the ORGANISATION and
   * outlives every task that wears it. Ticking a task off must not make the label picker stale,
   * and renaming a label must sweep every list — which is why the label mutations name both
   * prefixes on one line, exactly as applying a template does.
   */
  taskChecklist: (orgId: string, taskId: string) =>
    [...qk.task(orgId, taskId), 'checklist'] as const,
  taskActivity: (orgId: string, taskId: string) => [...qk.task(orgId, taskId), 'activity'] as const,
  taskLabels: (orgId: string) => [...qk.org(orgId), 'task-labels'] as const,
  /**
   * KOI Del 2 — kartet.
   *
   * ÉT PREFIKS FOR BEGGE, og det er derfor de er her sammen: å flytte et punkt endrer punktlista,
   * og å flytte senteret endrer hvor hvert eneste kart åpner. En skriving sveiper `qk.map` og
   * treffer begge — det er billigere enn to prefiks som må navngis riktig hver gang.
   *
   * LAGENE STÅR I NØKKELEN, sortert. To lagvalg er to svar, og en bufferrad som tjente begge ville
   * tegnet scenene under overskriften «oppgaver».
   */
  map: (orgId: string) => [...qk.org(orgId), 'map'] as const,
  editionMap: (orgId: string, editionId: string) =>
    [...qk.map(orgId), 'edition', editionId] as const,
  mapPoints: (orgId: string, editionId: string, types: readonly string[] | undefined) =>
    [...qk.map(orgId), 'points', editionId, types ?? 'all'] as const,
  taskLabelList: (orgId: string) => [...qk.taskLabels(orgId), 'list'] as const,
  /**
   * Tidslinja er ikke lista, og derfor ikke under `taskList`.
   *
   * Den spør om et VINDU og svarer uten markør — «alt som overlapper fra→til» — mens lista spør om
   * «de femti neste i denne rekkefølgen». To spørsmål, to svar, to buffernøkler. Vinduet står i
   * nøkkelen av samme grunn som `dueFrom`/`dueTo` gjør det i lista: to vinduer er to svar, og én
   * bufferrad som tjente begge ville tegnet forrige ukes stolper over denne ukas akse.
   */
  taskTimeline: (orgId: string, filters: TaskTimelineFilters) =>
    [...qk.tasks(orgId), 'timeline', filters] as const,
  /** Aktene i lineupen, som velgerne for ansvarlig trekker fra (217.8). */
  taskUnassignable: (orgId: string) => [...qk.tasks(orgId), 'unassignable'] as const,

  /**
   * Oppgavemaler — the organisation's standard sets of work (PAGES.md, Tasks [F1]).
   *
   * NOT UNDER `qk.tasks`, deliberately. Every task mutation invalidates that prefix by design, and
   * a template does not go stale because somebody ticked a task off. The reverse IS true — applying
   * a template creates tasks — and that mutation names both prefixes on one line, which says out
   * loud that one act invalidates two different things.
   */
  taskTemplates: (orgId: string) => [...qk.org(orgId), 'task-templates'] as const,
  taskTemplateList: (orgId: string) => [...qk.taskTemplates(orgId), 'list'] as const,

  /** Groups a task can be filed under. Reference tier: they change rarely, every list uses them. */
  departments: (orgId: string) => [...qk.org(orgId), 'departments'] as const,
  departmentList: (orgId: string) => [...qk.departments(orgId), 'list'] as const,

  /**
   * ONE EDITION'S OWN TREE — a different answer from `departmentList`, never the same entry.
   *
   * `departments` holds two kinds of row at once: the organisation's template (`edition_id is
   * null`), which is what Settings administers and what `departmentList` above addresses, and
   * each edition's own copy of it, written by `POST /v1/editions/:id/clone`. The contract makes
   * that a query parameter — `listDepartmentsQuerySchema.editionId`, absent meaning the template
   * — so the two are two requests with two different answers.
   *
   * NOT AN EXTENSION OF `departmentList`, DELIBERATELY. `[...departmentList(org), {editionId}]`
   * was the obvious shape and it is the wrong one: React Query matches by prefix, so
   * invalidating the template would sweep away every edition's tree with it, and — worse for a
   * reader — a stray `departmentList(org)` read would sit one element away from an entry holding
   * a completely different set of rows. `'edition', editionId` branches BESIDE `'list'` instead,
   * which makes the two structurally incapable of colliding.
   *
   * STILL UNDER `qk.departments(orgId)`, and that part is load-bearing: `useCloneEdition`
   * invalidates that prefix, and the clone's whole purpose is to write the rows this key
   * addresses. A key outside the prefix would leave the edition page showing an empty tree
   * seconds after the receipt said nine departments were created.
   */
  editionDepartmentList: (orgId: string, editionId: string) =>
    [...qk.departments(orgId), 'edition', editionId, 'list'] as const,

  /**
   * Inbox (Phase 1, page 3). `hasUnread` is a property of the READER — the caller's own
   * participant row — so a thread list is never shared between two people, and the org prefix is
   * what guarantees it is never carried across an organization switch either.
   */
  threads: (orgId: string) => [...qk.org(orgId), 'threads'] as const,
  threadList: (orgId: string, filters: ThreadListFilters) =>
    [...qk.threads(orgId), 'list', filters] as const,
  thread: (orgId: string, threadId: string) => [...qk.threads(orgId), 'detail', threadId] as const,
  /* Sammendraget i byråets interne rom (QA-runde 3 punkt 10). */
  threadBookingSummary: (orgId: string, threadId: string) =>
    [...qk.threads(orgId), 'booking-summary', threadId] as const,
  /* Saken bookingtråden gjelder (0932, punkt 19): kortet øverst i tråden. */
  threadCase: (orgId: string, engagementId: string) =>
    [...qk.threads(orgId), 'case', engagementId] as const,
  /**
   * `search` ER EN DEL AV NØKKELEN (rettelse B1).
   *
   * Søk i samtalen er et filter på den samme lista framfor en egen rute, så et treffsett og hele
   * tråden er to forskjellige svar på den samme spørringen. Uten søketeksten i nøkkelen ville det
   * ene skrevet over det andre i cachen — man søker, tømmer feltet, og får treffene tilbake som om
   * de var hele tråden.
   *
   * Tom streng er «ingen søk», og den nøkkelen er den alle de andre skrivingene invaliderer.
   */
  threadMessages: (orgId: string, threadId: string, search = '') =>
    [...qk.thread(orgId, threadId), 'messages', search] as const,
  /**
   * SIDEMENYENS TALL (KOI Del 4). Egen nøkkel og ikke en del av `threads`, fordi den invalideres
   * av noe annet: en melding i en tråd du IKKE er i endrer ingenting her, mens et varsel gjør det.
   * Å henge den under `threads` ville betydd at hver trådliste-invalidering også hentet tallene.
   */
  inboxUnreadCounts: (orgId: string) => [...qk.org(orgId), 'inbox-unread-counts'] as const,
  /**
   * VELGEREN  ÅPNER (KOI Del 4). Søketeksten er DEL AV nøkkelen, så hvert tastetrykk er sin
   * egen — det er det som gjør at å skrive en bokstav for mye og slette den igjen ikke koster en
   * ny runde. Samme form som enhver annen typeahead i huset.
   */
  linkSuggest: (orgId: string, q: string) => [...qk.org(orgId), 'link-suggest', { q }] as const,
  /** `@`-velgeren (rettelse B3): hele huset, og årgangen når tråden henger på en. */
  mentionSuggest: (orgId: string, q: string, editionId: string | null) =>
    [...qk.org(orgId), 'mention-suggest', { q, editionId }] as const,

  /** The Overview panels (Phase 1, page 4). Both feeds are the caller's own view of the org. */
  notifications: (orgId: string) => [...qk.org(orgId), 'notifications'] as const,
  notificationList: (orgId: string, unread: boolean) =>
    [...qk.notifications(orgId), 'list', { unread }] as const,
  notificationUnreadCount: (orgId: string) => [...qk.notifications(orgId), 'unread-count'] as const,

  activity: (orgId: string) => [...qk.org(orgId), 'activity'] as const,
  activityList: (orgId: string, limit: number) =>
    [...qk.activity(orgId), 'list', { limit }] as const,
  /**
   * ONE OBJECT'S OWN HISTORY — a different question from the organisation's feed, and therefore a
   * different entry rather than the feed's key with a filter appended.
   *
   * A contact's endringslogg and the Overview panel are read at the same time by the same admin,
   * and one cache entry for the pair would put the organisation's last eight writes under a
   * contact's heading. The entity pair is the discriminator, exactly as `performanceId` is for a
   * guest list.
   */
  activityFor: (orgId: string, entityType: string, entityId: string, limit: number) =>
    [...qk.activity(orgId), 'entity', { entityType, entityId, limit }] as const,

  /**
   * The permission matrix (Phase 2, Settings — the adjustable-permission switch panel).
   *
   * Org-scoped like every other key here, and on this one the prefix is doing more than
   * housekeeping: one organization's matrix says nothing about another's (`ROLES.md`, the note
   * above §1), so a matrix carried across an organization switch would be one customer's access
   * rules rendered under another customer's name.
   *
   * THE WHOLE MATRIX IS ONE KEY. `GET /v1/settings/permissions` answers with every row — fixed
   * and adjustable alike — and `PUT` answers with the matrix as it now stands rather than with
   * the one cell that moved. Nothing addresses a single row or a single role, so there is no
   * discriminator to carry: a per-row key would be a second copy of the same values and the
   * first one to go stale after a flip.
   */
  rolePermissions: (orgId: string) => [...qk.org(orgId), 'role-permissions'] as const,
  rolePermissionMatrix: (orgId: string) => [...qk.rolePermissions(orgId), 'matrix'] as const,
  /** «Hva artisten ser» (0452): byråets brytere, hele lista i én nøkkel — PUT svarer med alle. */
  artistView: (orgId: string) => [...qk.org(orgId), 'artist-view'] as const,
  /** D4: malen for artistdelingen, «Eksterne managere ser …». */
  artistShareTemplate: (orgId: string) => [...qk.org(orgId), 'artist-share-template'] as const,

  /**
   * Festivals and venues (fase 2, steg 2) — the sidebar list and one festival's own page.
   *
   * NO FILTER OBJECT, unlike contacts and tasks. `listFestivalsQuerySchema` offers a `kind`
   * parameter and nothing in the web app sends it: the sidebar asks for both kinds in one
   * request and groups the answer itself, exactly as the contract's own comment on that field
   * says it would. A `{ kind }` discriminator would be a key element that is permanently
   * `undefined` — a shape promising two caches where the app only ever fills one. `tagList` is
   * this shape for the same reason. The day something filters, the filter joins the key.
   *
   * The org prefix is not housekeeping here either: a festival name is the customer's own, and a
   * list carried across an organisation switch would put one customer's events in another's
   * menu.
   */
  festivals: (orgId: string) => [...qk.org(orgId), 'festivals'] as const,
  festivalList: (orgId: string) => [...qk.festivals(orgId), 'list'] as const,
  festival: (orgId: string, festivalId: string) =>
    [...qk.festivals(orgId), 'detail', festivalId] as const,

  /**
   * Editions (fase 2, steg 2 — the delivery after festivals). One årgang of a festival.
   *
   * THE FESTIVAL ID IS A DISCRIMINATOR, NOT A FILTER. `listEditionsQuerySchema.festivalId` is
   * optional at the API — absent means every edition in the organisation — and the festival page
   * always sends one, which the contract says out loud. So it is not a `{ filters }` object that
   * might grow a search box later: it is which festival this list IS, and two festivals are two
   * different answers that must never be served out of one cache entry.
   *
   * THE SORT IS NOT IN THE KEY, AND THAT IS THE DIFFERENCE FROM `taskList`. `EDITION_SORT_KEYS`
   * has exactly one member and the direction is fixed at descending — newest first, because
   * `ix_editions__org_festival_year` is `(organization_id, festival_id, year DESC, id DESC)` and
   * the cursor must match the index. There is one order, so there is nothing to tell apart.
   *
   * THE PREFIX IS WHAT `useCreateEdition` INVALIDATES, together with `qk.festivals(orgId)` —
   * `festivalDtoSchema.editionCount` is on the festival page header and on every sidebar row, so
   * a new årgang that refreshed only this list would leave two stale counts behind it.
   */
  editions: (orgId: string) => [...qk.org(orgId), 'editions'] as const,
  /**
   * `festivalId: null` is the ORGANISATION's årganger, all of them, and it is a different question
   * rather than a looser version of the same one — so it is a different key. The Tasks page's
   * «arrangement» filter needs every year the organisation has, across festivals; a festival's own
   * page needs that festival's. Serving one from the other's cache entry would put another
   * festival's years in a picker that promises this one's.
   */
  editionList: (orgId: string, festivalId: string | null) =>
    [...qk.editions(orgId), 'list', { festivalId }] as const,
  /**
   * One årgang, on its own page. Under the same prefix the list is, so a PATCH and a clone —
   * both of which invalidate `qk.editions(orgId)` — reach it without either mutation having to
   * learn a second key. `clonedFromEditionId` moves under a clone, and it is on this page.
   */
  edition: (orgId: string, editionId: string) =>
    [...qk.editions(orgId), 'detail', editionId] as const,

  /**
   * The calendar's per-day counts (fase 2, steg 3).
   *
   * ITS OWN DOMAIN, NOT AN EXTENSION OF `tasks`, even though what it counts is tasks. React
   * Query matches by prefix, and `qk.tasks(orgId)` is what every task mutation invalidates —
   * putting the counts under it would be correct today and a trap tomorrow, because the counts
   * are not a task list and the day this response grows the performances and shifts its own
   * contract header promises, a completed task would still be sweeping them away.
   *
   * It is refreshed on the same events instead: `useSetTaskStatus` and the rest name
   * `qk.calendar(orgId)` beside `qk.tasks(orgId)`, which says out loud that one act invalidates
   * two different things rather than hiding it inside a prefix.
   *
   * THE WINDOW IS THE DISCRIMINATOR. October and November are two answers, and `days` only
   * carries the days that have something — so a key that dropped the window would serve one
   * month's sparse list as another month's complete one, and the missing days would draw as
   * empty rather than as unknown.
   */
  calendar: (orgId: string) => [...qk.org(orgId), 'calendar'] as const,
  calendarWindow: (orgId: string, window: CalendarWindow) =>
    [...qk.calendar(orgId), 'window', window] as const,
  /**
   * KOI Del 3: ELEMENTENE, som er noe annet enn tellingene — og derfor en egen gren.
   *
   * `calendarWindow` er «hvor mange oppgaver har hver dag». Denne er «hva står i vinduet», over sju
   * kilder, med filtre. De to har ulik form, ulik ferskhet og ulike invalideringer, og en felles
   * nøkkel ville gjort en filterendring til en grunn til å hente tellingene på nytt.
   *
   * BEGGE LIGGER UNDER `qk.calendar(orgId)`, og det er med vilje: det ene stedet som må rammes av
   * «en oppgave ble flyttet» er kalenderen som helhet, og et prefiks er nettopp det verktøyet.
   */
  calendarItems: (orgId: string, query: Readonly<Record<string, unknown>>) =>
    [...qk.calendar(orgId), 'items', query] as const,
  /** Én hendelse, for panelet. Deltakerlista bor her og ikke i vinduet. */
  calendarEvent: (orgId: string, eventId: string) =>
    [...qk.calendar(orgId), 'event', eventId] as const,
  /** Mine levende abonnementslenker, for hele huset (`null`) eller én årgang. */
  calendarFeedTokens: (orgId: string, editionId: string | null) =>
    [...qk.calendar(orgId), 'feed-tokens', editionId] as const,

  /**
   * The line-up of one årgang (fase 2, steg 4).
   *
   * ITS OWN DOMAIN, NOT A BRANCH OF `editions`. A performance belongs to an årgang the way a task
   * belongs to one, and `qk.editions(orgId)` is what a PATCH on the årgang itself and the clone
   * both invalidate — putting the line-up under it would mean correcting a year's timezone swept
   * away every booking row with it. So `performances` is a sibling, and the edition id is a
   * discriminator inside it.
   *
   * THE EDITION ID SITS OUTSIDE `filters`, wrapped in an object exactly as `editionList` wraps its
   * festival id. It is not something the page might stop sending — it is which line-up this is —
   * and keeping it out of the filter object is what lets the stage menu share a cache entry with
   * the unfiltered list rather than minting a second one (see `performancesQueryOptions`).
   */
  performances: (orgId: string) => [...qk.org(orgId), 'performances'] as const,
  performanceList: (orgId: string, editionId: string, filters: PerformanceListFilters) =>
    [...qk.performances(orgId), 'list', { editionId }, filters] as const,

  /**
   * Stages (fase 2, steg 4 — second delivery), at the two scopes the one endpoint serves.
   *
   * THE ÅRGANG IS PART OF THE KEY BECAUSE IT CHANGES THE ANSWER, not because it narrows it.
   * `listStagesQuerySchema` says it in as many words: `festivalId` alone returns the festival's
   * STANDARD SET, and adding `editionId` returns that year's EFFECTIVE list — the standard set
   * with the year's overrides applied, every row carrying a `source`. Those are two different
   * lists of two different lengths, and the same stage appears in both wearing a different
   * `source`. One cache entry for the pair would put a year's tent in the festival's own
   * settings, or hide it from the year that put it up.
   *
   * `null` rather than an absent argument for the standard set, so the two scopes are two
   * distinguishable keys rather than one key with a hole in it.
   */
  stages: (orgId: string) => [...qk.org(orgId), 'stages'] as const,

  /**
   * Badge levels, per årgang. Two years are two different answers — a level lives in one year and
   * a shared cache entry would show last year's badges in this year's Settings.
   */
  /**
   * People [F2]. The årgang is a discriminator rather than a filter — two years are two crews —
   * and `understaffed` is in the shift key because it changes the ANSWER: the open half and the
   * whole plan must never come out of one cache entry.
   */
  /** «Er vi klare?» for one årgang — counted by the server, so the key is the year. */
  editionReadiness: (orgId: string, editionId: string) =>
    [...qk.editions(orgId), 'readiness', editionId] as const,

  crew: (orgId: string) => [...qk.org(orgId), 'crew'] as const,
  crewList: (orgId: string, editionId: string, departmentId: string | null) =>
    [...qk.crew(orgId), 'list', { editionId, departmentId }] as const,

  /**
   * «Legg til ansatte»-lista (sak 182): medlemmer UTEN tilgang til årgangen.
   *
   * UNDER `qk.crew` MED VILJE, selv om den svarer med tilganger og ikke med bemanning. Én
   * avkryssing skriver begge — Atles punkt 4, «får både tilgang og står på bemanningen» — så en
   * invalidering som bare traff det ene ville latt den andre lista stå med gårsdagens svar. Under
   * det samme prefikset er «skriv én gang, oppdater begge» det som skjer av seg selv.
   */
  editionAccessCandidates: (orgId: string, editionId: string) =>
    [...qk.crew(orgId), 'access-candidates', editionId] as const,
  shifts: (orgId: string) => [...qk.org(orgId), 'shifts'] as const,

  /**
   * Poster (PEOPLE.md, fane 1). Scoped by year like the departments they sit in: the template and
   * a year’s own list are two different answers, and one entry for the pair would draw the wrong
   * group headings on the timeline.
   */
  staffingRoles: (orgId: string, editionId: string | null) =>
    /*
     * UNDER `qk.shifts` SIDEN 0357. En ressurs som får en eier lager postene sine, og «Deles ut på
     * vakta» skriver om vaktene — så en skriving på den ene siden må friske opp den andre. Ett
     * prefiks er én linje å huske framfor to.
     */
    [...qk.shifts(orgId), 'staffing-roles', { editionId }] as const,
  /** «Mine vakter» (201.14): mine, på tvers av årganger når `editionId` mangler. */
  myShifts: (orgId: string, editionId: string | null) =>
    [...qk.shifts(orgId), 'mine', { editionId }] as const,
  /**
   * PÅMELDING OG BYTTE (QA16 297.4), under `qk.shifts`: en godkjenning bemanner vakta, og en ny vakt
   * kan være ledig. Ett prefiks friskes av begge sider.
   */
  shiftRequests: (orgId: string) => [...qk.shifts(orgId), 'requests'] as const,
  openShifts: (orgId: string) => [...qk.shiftRequests(orgId), 'open-shifts'] as const,
  myShiftRequests: (orgId: string) => [...qk.shiftRequests(orgId), 'mine'] as const,
  pendingShiftRequests: (orgId: string, nightId: string | null) =>
    [...qk.shiftRequests(orgId), 'pending', { nightId }] as const,
  shiftSwapColleagues: (orgId: string, shiftId: string) =>
    [...qk.shiftRequests(orgId), 'colleagues', shiftId] as const,
  /** The window is in the key: two windows are two answers, not two pages of one. */
  shiftList: (
    orgId: string,
    editionId: string,
    understaffed: boolean,
    from?: string,
    to?: string,
  ) => [...qk.shifts(orgId), 'list', { editionId, understaffed, from, to }] as const,
  /** Vaktene uten post i hele årgangen (bølge 0, A15) — under `qk.shifts`, så hver skriving friskes. */
  looseShifts: (orgId: string, editionId: string) =>
    [...qk.shifts(orgId), 'loose', { editionId }] as const,

  /**
   * Frivillige (PEOPLE.md, fane 2).
   *
   * THE FILTERS ARE IN THE KEY because they change the answer the server computes rather than
   * narrowing one it already sent — the list is keyset-paged, so «bare ventende» is a different
   * page rather than a subset of this one. The summary carries only `formId`: it counts a whole
   * year and is deliberately unaffected by the list's status filter, so somebody reading «27
   * ventende» while looking at the approved list still sees the true number.
   */
  volunteers: (orgId: string) => [...qk.org(orgId), 'volunteers'] as const,
  volunteerList: (
    orgId: string,
    editionId: string,
    filters: {
      status: string | null;
      source: string | null;
      formId: string | null;
      q: string;
      sort: string;
      direction: string;
    },
  ) => [...qk.volunteers(orgId), 'list', { editionId, ...filters }] as const,
  volunteer: (orgId: string, id: string) => [...qk.volunteers(orgId), 'one', id] as const,
  volunteerSummary: (orgId: string, editionId: string, formId: string | null) =>
    [...qk.volunteers(orgId), 'summary', { editionId, formId }] as const,
  /** Alle årgangers skjemalister: en skriving vet skjemaets id, ikke hvilken årgang lista gjelder. */
  signupFormsAll: (orgId: string) => [...qk.org(orgId), 'signup-forms'] as const,
  signupForms: (orgId: string, editionId: string) =>
    [...qk.signupFormsAll(orgId), { editionId }] as const,

  /**
   * Akkrediteringslista (PEOPLE.md, fane 3).
   *
   * The FILTERS are in the key because the server computes the counters over the filtered list —
   * see the service for why those follow the filter and the volunteer page's do not. Two filter
   * settings are two different answers, not one answer narrowed.
   */
  accreditations: (orgId: string) => [...qk.org(orgId), 'accreditations'] as const,
  accreditationList: (
    orgId: string,
    editionId: string,
    filters: {
      accreditationTypeId: string | null;
      state: string | null;
      q: string;
      sort: string | null;
      direction: string;
    },
  ) => [...qk.accreditations(orgId), 'list', { editionId, ...filters }] as const,

  accreditationLevels: (orgId: string) => [...qk.org(orgId), 'accreditation-levels'] as const,
  accreditationLevelList: (orgId: string, editionId: string) =>
    [...qk.accreditationLevels(orgId), 'list', { editionId }] as const,
  /* Passtypene (#25a). Eget prefiks: de er sin egen liste, selv om de deler skjerm og celler. */
  accreditationPasses: (orgId: string) => [...qk.org(orgId), 'accreditation-passes'] as const,
  accreditationPassList: (orgId: string, editionId: string) =>
    [...qk.accreditationPasses(orgId), 'list', { editionId }] as const,
  stageList: (orgId: string, festivalId: string, editionId: string | null) =>
    [...qk.stages(orgId), 'list', { festivalId, editionId }] as const,

  /**
   * One booking, on its own page (fase 2, steg 4 — the performance detail page).
   *
   * UNDER `qk.performances(orgId)`, beside the list, exactly as `qk.task` sits beside `taskList`.
   * That is load-bearing rather than tidy: every write to a performance invalidates that prefix,
   * and a detail key outside it would leave this page showing the status the booking had before
   * the chain on it was pressed.
   */
  performance: (orgId: string, performanceId: string) =>
    [...qk.performances(orgId), 'detail', performanceId] as const,

  /**
   * The guest list of one booking, and the two numbers the door reads (fase 2, steg 4).
   *
   * ITS OWN DOMAIN, NOT A BRANCH OF `performances`, and the argument is `calendar`'s one level up.
   * React Query matches by prefix and `qk.performances(orgId)` is what every booking write
   * invalidates — so a status moved along the chain would throw away every guest list and every
   * totals entry in the cache to refetch data that had not moved. Nothing goes the other way
   * either: `performanceDtoSchema` carries no guest count, so a name added to a list changes
   * nothing on the booking, and these keys are invalidated on their own.
   *
   * THE PERFORMANCE ID IS A DISCRIMINATOR, NOT A FILTER. `listPerformanceGuestsQuerySchema` makes
   * it REQUIRED — "a guest list without a performance is not a thing this holds" — so it is which
   * list this IS, and two bookings are two answers that must never come out of one entry.
   *
   * THE TOTALS BRANCH BESIDE `'list'` RATHER THAN UNDER IT. `GET /v1/performances/:id/guest-totals`
   * is an aggregate over the whole list and a page is a page of it; nesting the sums under the
   * list's key would put a number and the rows it is not derived from one element apart, and the
   * filtered list would sit between them.
   */
  /**
   * Menneskene rundt én act (PERFORMANCE.md, fane People).
   *
   * ITS OWN DOMAIN RATHER THAN A BRANCH OF `performances`, for exactly the reason the guest list
   * gives below: React Query matches by prefix, `qk.performances(orgId)` is what every booking
   * write invalidates, and a status moved along the chain would otherwise throw away every crew
   * list in the cache to refetch data that had not moved. Nothing goes the other way either — the
   * performance DTO carries no head count.
   *
   * THE PERFORMANCE ID IS A DISCRIMINATOR, NOT A FILTER: the contract makes it required, so it is
   * which list this IS, and two bookings are two answers that must never share an entry.
   */
  performancePeople: (orgId: string) => [...qk.org(orgId), 'performance-people'] as const,
  performancePeopleOf: (orgId: string, performanceId: string) =>
    [...qk.performancePeople(orgId), { performanceId }] as const,

  /** Reise for én act (PERFORMANCE.md, fane Travel). Its own domain, for the reason above. */
  performanceTravel: (orgId: string) => [...qk.org(orgId), 'performance-travel'] as const,
  performanceTravelOf: (orgId: string, performanceId: string) =>
    [...qk.performanceTravel(orgId), { performanceId }] as const,

  /** Overnatting for én act (PERFORMANCE.md, fane 5). Its own domain, for the reason above. */
  performanceStays: (orgId: string) => [...qk.org(orgId), 'performance-stays'] as const,
  performanceStaysOf: (orgId: string, performanceId: string) =>
    [...qk.performanceStays(orgId), { performanceId }] as const,

  performanceGuests: (orgId: string) => [...qk.org(orgId), 'performance-guests'] as const,
  performanceGuestList: (orgId: string, performanceId: string, filters: GuestListFilters) =>
    [...qk.performanceGuests(orgId), 'list', { performanceId }, filters] as const,
  performanceGuestTotals: (orgId: string, performanceId: string) =>
    [...qk.performanceGuests(orgId), 'totals', { performanceId }] as const,

  /**
   * The advance of one booking — logistics, accommodation and hospitality.
   *
   * ONE ENTRY FOR THREE SECTIONS, because there is one request behind them: the API answers all
   * fifteen fields at once, and a key per section would make three cache entries out of one
   * response and leave two of them stale after every save.
   *
   * ITS OWN BRANCH, NOT UNDER `qk.performances(orgId)`, for the reason the guest keys sit apart:
   * `PerformanceDto` carries nothing from the advance, so a saved hotel changes nothing on the
   * booking — and a status move along the chain invalidates `qk.performances(orgId)` and would
   * otherwise throw away every advance in the cache to refetch data that had not moved.
   */
  /**
   * Every booking of one ARTIST, across every year — what the contact's own page reads (sak 49).
   *
   * Under `qk.performances(orgId)` rather than beside it, unlike the advance: this really is a
   * page of the line-up, narrowed by a filter the endpoint already understands, so a booking moved
   * along the chain should invalidate it along with every other view of that row.
   */
  artistBookings: (orgId: string, contactId: string) =>
    [...qk.performances(orgId), 'by-artist', { contactId }] as const,

  /**
   * The files on one thing (sak 54).
   *
   * Its own branch rather than under the owner's, because an attachment hangs on two different kinds
   * of owner: nesting it under `qk.tasks` would leave the message ones homeless, and two branches
   * for one endpoint is two prefixes an upload would have to invalidate.
   *
   * The owner PAIR is the discriminator. Two tasks' files are two answers and must never come out of
   * one entry — the same rule `performanceGuestList` follows for its booking.
   */
  attachments: (orgId: string) => [...qk.org(orgId), 'attachments'] as const,
  attachmentsOf: (orgId: string, ownerType: string, ownerId: string) =>
    [...qk.attachments(orgId), { ownerType, ownerId }] as const,
  /*
   * INFO PÅ RESSURS, POST OG VAKT (0925, #51). Lenkene per eier, og det samlede kortet for en vakt
   * under vaktene, så det som feier vaktplanen feier kortet. Vedleggene er `attachmentsOf`.
   */
  infoLinks: (orgId: string) => [...qk.org(orgId), 'info-links'] as const,
  infoLinksOf: (orgId: string, ownerType: string, ownerId: string) =>
    [...qk.infoLinks(orgId), { ownerType, ownerId }] as const,
  shiftInfo: (orgId: string, shiftId: string) => [...qk.shifts(orgId), 'info', shiftId] as const,
  /* «Kontaktpersoner hos festivalen» (109), under programpostens advance. */
  festivalContacts: (orgId: string, performanceId: string) =>
    [...qk.festivalContactsAll(orgId), performanceId] as const,
  /*
   * Prefikset for alle postenes lister (K4 F10): en person lagt til for hånd under «Andre fra
   * festivalen» ER en kontaktperson, og lista sto uten hen til siden ble lastet på nytt.
   */
  festivalContactsAll: (orgId: string) =>
    [...qk.performanceAdvance(orgId), 'festival-contacts'] as const,
  /* «Hent fra organisasjonen» (29): forhåndsvisningen, under avdelingene. */
  departmentImportPreview: (orgId: string, editionId: string) =>
    [...qk.departments(orgId), 'import-preview', editionId] as const,

  performanceAdvance: (orgId: string) => [...qk.org(orgId), 'performance-advance'] as const,
  performanceAdvanceOf: (orgId: string, performanceId: string) =>
    [...qk.performanceAdvance(orgId), { performanceId }] as const,

  /**
   * HVILKE AV HUSETS DOKUMENTER SOM FØLGER ADVANCEN (vedtak 113.2).
   *
   * UNDER `performanceAdvance`, fordi et svar her ER en endring i advancen: fila havner i Teknisk,
   * og seksjonen skal tegnes på nytt. Den motsatte veien holder også — en ny seksjon kan flytte
   * hvor fila ligger — så det er riktig at den ene prefiksen sveiper begge.
   */
  advanceHouseDocuments: (orgId: string, performanceId: string) =>
    [...qk.performanceAdvance(orgId), 'house-documents', { performanceId }] as const,

  /**
   * The delivery groups, which are org-level Settings and NOT under an act.
   *
   * Renaming one renames it on every performance, so a write here invalidates every advance in the
   * cache — but the reverse must not hold: an act going stale cannot throw away a list the whole
   * organisation shares and the Settings screen edits nowhere near a booking.
   */
  advanceDeliveryCategories: (orgId: string) =>
    [...qk.org(orgId), 'advance-delivery-categories'] as const,

  /**
   * Kontrakt. Its own prefix, and a write here also sweeps the GUEST LIST: the agreed seat count
   * caps what People may add, so a raised cap has to reach the tab that reads it.
   */
  performanceContract: (orgId: string) => [...qk.org(orgId), 'performance-contract'] as const,
  performanceContractOf: (orgId: string, performanceId: string) =>
    [...qk.performanceContract(orgId), { performanceId }] as const,
  /** Byråets forslag på kontrakten (222.4). Under kontraktens prefiks, så et godtatt forslag feier begge. */
  contractProposals: (orgId: string, performanceId: string) =>
    [...qk.performanceContract(orgId), 'proposals', performanceId] as const,

  /**
   * Oversikt. VOLATILE, unlike every other tab on the page: its numbers are derived from six
   * others, so a room assigned in Overnatting has to change a counter here. It owns nothing but
   * the manual timetable items, and everything else it draws belongs to somebody else.
   */
  performanceOverview: (orgId: string) => [...qk.org(orgId), 'performance-overview'] as const,
  performanceOverviewOf: (orgId: string, performanceId: string) =>
    [...qk.performanceOverview(orgId), { performanceId }] as const,

  /**
   * Scenetidene showdagene kjøres på (sak 68.2), PER ÅRGANG siden K-1.
   *
   * Avsnittet her sa: «Org data with no edition below it: what a festival calls "push back" is the
   * same word in 2027 and 2028, and a list per year would mean typing it again every year.»
   * Migrering 0257 flyttet settet til årgangen, og 0258 lot en ny årgang kopiere forrige — så
   * ingen skriver det igjen, og 2028 kan likevel ha et punkt 2027 ikke hadde.
   *
   * Den nakne `stageTimeKinds(orgId)` står igjen som PREFIKSET en skriving feier på: én
   * invalidering treffer hver årgangs liste, og en skriving trenger ikke vite hvilke årganger
   * leseren har åpne.
   */
  stageTimeKinds: (orgId: string) => [...qk.org(orgId), 'stage-time-kinds'] as const,
  stageTimeKindsOf: (orgId: string, editionId: string) =>
    [...qk.stageTimeKinds(orgId), { editionId }] as const,

  /** Hva en person er akkreditert som (sak 57). Per årgang, som scenetidene. */
  accreditationTypes: (orgId: string) => [...qk.org(orgId), 'accreditation-types'] as const,
  accreditationTypesOf: (orgId: string, editionId: string) =>
    [...qk.accreditationTypes(orgId), { editionId }] as const,

  /**
   * Deling (sak 232, T3): hvem ett objekt er delt med. Én oppføring per objekt; prefikset `shares`
   * er det en skriving feier, siden en lenkeperson har én lenke for alt som er delt i årgangen.
   */
  shares: (orgId: string) => [...qk.org(orgId), 'shares'] as const,
  sharesOf: (orgId: string, objectType: string, objectId: string) =>
    [...qk.shares(orgId), { objectType, objectId }] as const,
  /** F9: oversikten «Delinger». Under `shares`, så hver deling, endring og tilbaketrekking feier den. */
  sharesOverview: (orgId: string, filters: Record<string, unknown>) =>
    [...qk.shares(orgId), 'overview', filters] as const,
  /* F34: masseutsending. Prefikset feies når noe er sendt; lista spør igjen mens noe venter. */
  broadcasts: (orgId: string) => [...qk.org(orgId), 'broadcasts'] as const,
  broadcastOptions: (orgId: string, festivalId: string, editionId: string | null) =>
    [...qk.broadcasts(orgId), 'options', festivalId, editionId] as const,
  broadcastPreview: (orgId: string, request: Record<string, unknown>) =>
    [...qk.broadcasts(orgId), 'preview', request] as const,
  broadcastList: (orgId: string, festivalId: string, editionId: string | null) =>
    [...qk.broadcasts(orgId), 'list', festivalId, editionId] as const,
  broadcastDetail: (orgId: string, broadcastId: string) =>
    [...qk.broadcasts(orgId), 'detail', broadcastId] as const,
  /* QA13 bølge B (B-send): e-posten for én mottaker (45) og korrespondansen på personen (46). */
  broadcastEmailPreview: (orgId: string, request: Record<string, unknown>) =>
    [...qk.broadcasts(orgId), 'preview-email', request] as const,
  correspondence: (orgId: string, personId: string) =>
    [...qk.broadcasts(orgId), 'correspondence', personId] as const,
  /* Runde 4. oktober, pakke 2 (E): under `shares`, så hver delingsskriving feier dem også. */
  /** F6: hvor mange som mister tilgangen når objektet slettes. */
  shareCount: (orgId: string, objectType: string, objectId: string) =>
    [...qk.shares(orgId), 'count', { objectType, objectId }] as const,
  /** D3: «Hvem har tilgang» på en programpost. Under `shares`, så hver deling frisker den. */
  performanceAccess: (orgId: string, performanceId: string, advance: boolean) =>
    [...qk.shares(orgId), 'access', { performanceId, advance }] as const,
  /** Kolonnen «Tilgang» i listene: sammendraget for programpostene på siden (ADVANCE-TILGANG §6). */
  accessSummaries: (orgId: string, performanceIds: readonly string[]) =>
    [...qk.shares(orgId), 'accessSummaries', { performanceIds: [...performanceIds].sort() }] as const,
  /** F10: de andre objektene av samme type som kan deles i samme slengen. */
  shareSiblings: (orgId: string, objectType: string, objectId: string) =>
    [...qk.shares(orgId), 'siblings', { objectType, objectId }] as const,
  /** F8: delingskortene i en tråd, for organisasjonen som delte. */
  threadShares: (orgId: string, threadId: string) =>
    [...qk.shares(orgId), 'thread', threadId] as const,

  /**
   * The folder tree of one ZONE (Documents [F2]). `editionId` is null for the organisation's own
   * shelf — a different answer rather than a filter of the same one, exactly as the two pages are
   * two menu entries.
   *
   * The FILES inside a folder are `attachmentsOf(orgId, 'folder', folderId)`: the same key the
   * task panel uses, because they are the same rows through the same route.
   */
  /** One object's own changelog — the årgang's Overview asks it about the edition row. */
  objectActivityList: (orgId: string, entityType: string, entityId: string, limit: number) =>
    [...qk.activity(orgId), 'object', { entityType, entityId, limit }] as const,

  /**
   * Bookingregisteret — artistene, byråene, utkastene og budene (Atle, 3. september).
   *
   * EGEN PREFIKS OG IKKE UNDER `qk.booking`, selv om siden heter det samme. De to leser ingenting
   * felles: nabosidens tall kommer fra `performances` og `cost_lines`, disse fra fem tabeller ingen
   * av dem rører. Under én prefiks ville hvert nye bud tvunget en refetch av traktoversikten, og
   * hvert statusbytte på en programpost tvunget en refetch av hele registeret.
   *
   * `register` er PAGET og har derfor filtrene sine i nøkkelen; `agencies` er det ikke, fordi den
   * er en oppslagsliste bak et felt. `board` står for seg med utkastets id, siden det er ETT svar
   * som holder dager, scener og plasseringer sammen — se kontrakten.
   */
  bookingRegister: (orgId: string) => [...qk.org(orgId), 'booking-register'] as const,
  bookingRegisterList: (
    orgId: string,
    filters: {
      search?: string | undefined;
      status?: string | undefined;
      agencyId?: string | undefined;
      shortlisted?: boolean | undefined;
      gender?: string | undefined;
      priceConfirmed?: boolean | undefined;
      minCostMinor?: number | undefined;
      maxCostMinor?: number | undefined;
      sort?: string | undefined;
      direction?: string | undefined;
    },
  ) => [...qk.bookingRegister(orgId), 'list', filters] as const,
  bookingRegisterArtist: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'artist', artistId] as const,
  bookingAgencies: (orgId: string) => [...qk.bookingRegister(orgId), 'agencies'] as const,
  /** Byråets stamdata om én artist (vedtak 96.1): bandet, riderne og promoen, i ett svar. */
  artistRoster: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'roster', artistId] as const,
  /** Reisefølget på én booking (0378): byråets egen (`agency:<id>`) eller en forespørsel (`enquiry:<id>`). */
  bookingParty: (orgId: string, booking: string) =>
    [...qk.bookingRegister(orgId), 'party', booking] as const,
  /**
   * ALLE artistenes stamdata, som prefiks.
   *
   * Å bytte format på en forespørsel endrer noe rosteren viser — formatkolonnen i bookingfanen
   * leser rosterens formater for å finne reisefølget — men den som bytter står i innboksen og
   * vet ikke hvilken artist-id det gjelder uten et oppslag. Prefikset feier dem alle, og de
   * hentes bare på nytt om noen faktisk ser på en av dem.
   */
  artistRosterAll: (orgId: string) => [...qk.bookingRegister(orgId), 'roster'] as const,
  /** Markedsplassen (migrering 0097): tilbudene fra ANDRE hus, filtrert på søket. */
  marketplaceAll: (orgId: string) => [...qk.org(orgId), 'marketplace'] as const,
  marketplace: (orgId: string, search: string) =>
    [...qk.marketplaceAll(orgId), { search }] as const,
  /** Giggene artisten har hos arrangørene (migrering 0096) — festivalens rader, lest av byrået. */
  artistGigs: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'gigs', artistId] as const,
  /**
   * BYRAAETS EGEN ANDEL AV AARETS HONORARER (vedtak 102.1).
   *
   * Under registerets prefiks, fordi den flytter seg av to ting registeret eier: et nytt godtatt
   * bud, og satsen i husets innstillinger. En artist som skrives sveiper den dermed med seg.
   */
  artistCommission: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'commission', artistId] as const,
  /**
   * AVTALEN OG SPERREPERIODENE (ARTIST-AVTALE-ORDREN Del 1) — ETT SVAR, TRE LESERE.
   *
   * Avtale-fanen, kalenderen og forespørselsmodalen leser den samme nøkkelen. To nøkler ville gitt
   * to øyeblikk: en agent som nettopp la inn en sperreperiode ville sett den i kalenderen og ikke
   * i advarselen. Under registerets prefiks, som resten av artistens ting.
   */
  artistAgreement: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'agreement', artistId] as const,
  /** Byråets egne notater om artisten (Del 2). Aldri delt — ingen delt flate leser nøkkelen. */
  artistNotes: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'notes', artistId] as const,
  /**
   * BOOKINGENE BYRAAET HAR SKREVET INN SELV (QA-runde 12, #35, migrering 0283).
   *
   * Under registerets prefiks, som resten av artistens ting: en booking byraaet fører er ikke en
   * forespoersel, men den staar i den samme lista og den samme kalenderen, og en invalidering av
   * registeret skal ta den med seg.
   */
  agencyBookings: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'agency-bookings', artistId] as const,
  bookingDrafts: (orgId: string, editionId: string) =>
    [...qk.bookingRegister(orgId), 'drafts', { editionId }] as const,
  /* Utkastlistene i alle årganger — det en sletting feier, uten å treffe brettet (QA5 E #12). */
  bookingDraftsAll: (orgId: string) => [...qk.bookingRegister(orgId), 'drafts'] as const,
  /** Dialogen — all kontakt med byråene, filtrert. Nøkkelen bærer filteret, som resten av huset. */
  bookingDialogue: (orgId: string, query: Readonly<Record<string, unknown>>) =>
    [...qk.bookingRegister(orgId), 'dialogue', query] as const,
  bookingBoard: (orgId: string, draftId: string) =>
    [...qk.bookingRegister(orgId), 'board', draftId] as const,
  /* Spillestedets sesong (QA13 25). Under registeret, så et bud som endres, feier den også. */
  bookingSeason: (orgId: string, festivalId: string, year: number) =>
    [...qk.bookingRegister(orgId), 'season', festivalId, year] as const,
  bookingBids: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'bids', artistId] as const,
  /* Dyplenken `/booking?bid=` / `?enquiry=` slått opp til budet og artisten (QA5 E #2). */
  bookingBidLookup: (orgId: string, by: 'bid' | 'enquiry', id: string) =>
    [...qk.bookingRegister(orgId), 'bid-lookup', by, id] as const,
  /* Tråden — samtalen med byrået om én artist. Under budene, fordi et nytt bud endrer den. */
  bookingThread: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'thread', artistId] as const,
  bookingPlacements: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'placements', artistId] as const,
  /* QA15 C3: salens kø på en dato, under budene, fordi et nytt bud endrer den. */
  bookingRoomQueue: (orgId: string, roomId: string, date: string, nightId?: string) =>
    [...qk.bookingRegister(orgId), 'room-queue', roomId, date, nightId ?? null] as const,
  /* Innstillinger, ikke en booking — men under samme prefiks, fordi et nytt bud arver dem og en
     endring skal nå budskjemaet uten en omlasting. */
  bookingTermDefaults: (orgId: string) => [...qk.bookingRegister(orgId), 'term-defaults'] as const,
  /* Husets satser for reise og opphold, og vektene booking score bruker (0072, 0075). */
  bookingPricingSettings: (orgId: string) =>
    [...qk.bookingRegister(orgId), 'pricing-settings'] as const,
  bookingPrices: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'prices', artistId] as const,
  /* Soundcharts-dataene i artistpanelet (sak 273): Publikum, Konserter og plattformraden. */
  bookingSoundcharts: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'soundcharts', artistId] as const,
  /* Hvem som har representert artisten, og når vi så det (0073). */
  bookingAgencyHistory: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'agency-history', artistId] as const,
  /*
   * «Overganger» (0438): lista og månedene. Under registeret, så en ny import rydder alt. «Må
   * sjekkes» bor i Control Room (sak 247), ikke her.
   */
  bookingAgencyMoves: (orgId: string) => [...qk.bookingRegister(orgId), 'agency-moves'] as const,
  bookingAgencyMovesList: (orgId: string, filters: Readonly<Record<string, unknown>>) =>
    [...qk.bookingAgencyMoves(orgId), 'list', filters] as const,
  bookingAgencyMovesMonths: (orgId: string, filters: Readonly<Record<string, unknown>>) =>
    [...qk.bookingAgencyMoves(orgId), 'months', filters] as const,

  /**
   * Økonomi. THREE builders, because the page asks three questions with different lifetimes: the
   * summary is per year, the chart of accounts is per organisation, and the lines are a paged list
   * with filters. A single key would make entering one line refetch the chart.
   */
  economy: (orgId: string) => [...qk.org(orgId), 'economy'] as const,
  economySummary: (orgId: string, editionId: string) =>
    [...qk.economy(orgId), 'summary', { editionId }] as const,
  costCategories: (orgId: string) => [...qk.economy(orgId), 'categories'] as const,
  costLines: (orgId: string) => [...qk.economy(orgId), 'lines'] as const,
  costLineList: (
    orgId: string,
    editionId: string,
    filters: { performanceId?: string; categoryId?: string; kind?: string },
  ) => [...qk.costLines(orgId), 'list', { editionId, ...filters }] as const,
  /**
   * Byråets egne linjer på én henvendelse (sak 115).
   *
   * Egen nøkkel og ikke `costLineList` med et ekstra felt: de to listene har ulikt ANKER, og en
   * nøkkel der `editionId` er `undefined` ville kollidert med «alle linjer i huset» første gang
   * noen glemte å sende det.
   */
  /** Byråets egen profil om den samme artisten (sak 116.2). Under artisten, ikke under lista. */
  agencyArtistProfile: (orgId: string, artistId: string) =>
    [...qk.bookingRegisterArtist(orgId, artistId), 'agency-profile'] as const,
  enquiryCostLines: (orgId: string, bookingEnquiryId: string) =>
    [...qk.costLines(orgId), 'enquiry', bookingEnquiryId] as const,

  /**
   * Oppsummering. ITS OWN PREFIX, and NOT under `economy`, even though Økonomi is one of its tabs.
   *
   * The four tabs are one page to a reader and four different questions to the cache. Logistics,
   * stay and food are read from journeys, beds and dinners that live on the bookings — so what
   * invalidates them is a travel or stay mutation on a performance, and not a cost line. Filing an
   * invoice must not refetch the dinner list, and moving a flight must not refetch the budget.
   *
   * The economy tab keeps reading `qk.economySummary` above: it is the same data as before, shown
   * in a new frame.
   */
  summary: (orgId: string) => [...qk.org(orgId), 'summary'] as const,
  summaryTab: (orgId: string, tab: 'logistics' | 'stay' | 'food', editionId: string) =>
    [...qk.summary(orgId), tab, { editionId }] as const,

  /**
   * Chatten på én programpost (fane 9).
   *
   * TWO KEYS UNDER ONE PREFIX, because the tab bar and the panel ask different questions with
   * different lifetimes: the STATE — «is there anything unread here» — is drawn by every reader of
   * the act whether or not they open the chat, and the MESSAGES are an infinite list nobody loads
   * until they do. One key would make the tab marker refetch a hundred messages.
   *
   * `performanceChat(orgId)` is the prefix a post invalidates: a new message changes both answers,
   * and it is also what the Inbox's own `qk.threads` prefix does NOT cover — a chat message moves
   * a thread the Inbox has cached, so the mutation names both.
   */
  performanceChat: (orgId: string) => [...qk.org(orgId), 'performance-chat'] as const,
  /**
   * BOOKINGCHATTEN, samme form ett hakk til siden.
   *
   * Egen prefiks og ikke en delt: en melding om en artist skal ikke tvinge programpostens
   * chatter til å hente på nytt. Formen under er identisk, fordi det er den samme motoren.
   */
  /* Byråets innboks. Egen prefiks: en henvendelse er ikke festivalens register. */
  agencyEnquiries: (orgId: string) => [...qk.org(orgId), 'agency-enquiries'] as const,
  agencyEnquiriesList: (orgId: string, filters: Readonly<Record<string, unknown>>) =>
    [...qk.agencyEnquiries(orgId), 'list', filters] as const,
  agencyEnquiry: (orgId: string, enquiryId: string) =>
    [...qk.agencyEnquiries(orgId), 'one', enquiryId] as const,
  /* 251.5: alle forespørslene til én artist, alle sider — artistsiden. Under forespørslene. */
  agencyArtistEnquiries: (orgId: string, artistId: string) =>
    [...qk.agencyEnquiries(orgId), 'artist', artistId] as const,
  /* F24: arrangørene til filteret i innboksen. Under forespørslene, så et svar feier den med. */
  agencyEnquiryOrganizers: (orgId: string) => [...qk.agencyEnquiries(orgId), 'organizers'] as const,
  /*
   * Byråets solgte konserter i «Konserter» (sak 233). Under forespørslene og ikke under konsertene:
   * raden står og faller med byråets eget svar på budet, og det svaret feier dette prefikset.
   */
  soldConcerts: (orgId: string, filters: Readonly<Record<string, unknown>>) =>
    [...qk.agencyEnquiries(orgId), 'sold-concerts', filters] as const,
  bookingChat: (orgId: string) => [...qk.org(orgId), 'booking-chat'] as const,
  bookingChatOf: (orgId: string, artistId: string) =>
    [...qk.bookingChat(orgId), 'state', { artistId }] as const,
  bookingChatMessagesAll: (orgId: string, artistId: string) =>
    [...qk.bookingChat(orgId), 'messages', artistId] as const,
  bookingChatMessages: (orgId: string, artistId: string, search = '') =>
    [...qk.bookingChatMessagesAll(orgId, artistId), { search }] as const,
  bookingChatMentions: (orgId: string, artistId: string, q = '') =>
    [...qk.bookingChat(orgId), 'mentions', { artistId, q }] as const,
  /**
   * Samtalen om ETT engasjement (migrering 0099): budet hos festivalen, henvendelsen hos byrået.
   *
   * Egen prefiks, ikke under bookingchatten: to arrangører som spør om den samme artisten er to
   * samtaler, og en melding i den ene skal ikke få den andre til å hente på nytt.
   */
  engagementChat: (orgId: string) => [...qk.org(orgId), 'engagement-chat'] as const,
  /* Bookingsaken selv (0927) med porten for det motparten eier (0930). */
  engagement: (orgId: string, engagementId: string) =>
    [...qk.org(orgId), 'engagement', engagementId] as const,
  engagementChatOf: (orgId: string, engagementId: string) =>
    [...qk.engagementChat(orgId), 'state', { engagementId }] as const,
  engagementChatMessagesAll: (orgId: string, engagementId: string) =>
    [...qk.engagementChat(orgId), 'messages', engagementId] as const,
  engagementChatMessages: (orgId: string, engagementId: string, search = '') =>
    [...qk.engagementChatMessagesAll(orgId, engagementId), { search }] as const,
  engagementChatMentions: (orgId: string, engagementId: string, q = '') =>
    [...qk.engagementChat(orgId), 'mentions', { engagementId, q }] as const,
  /* Chatten på bookingen (persontypene §2.3): tilgangen, åpningen og samtalen adressert med tråden. */
  bookingChats: (orgId: string) => [...qk.org(orgId), 'booking-chats'] as const,
  bookingChatAccess: (orgId: string, kind: string, id: string) =>
    [...qk.bookingChats(orgId), 'access', { kind, id }] as const,
  bookingChatAccessByThread: (orgId: string, threadId: string) =>
    [...qk.bookingChats(orgId), 'access', { threadId }] as const,
  bookingChatsClosedSoon: (orgId: string, artistId: string) =>
    [...qk.bookingChats(orgId), 'closed-soon', artistId] as const,
  bookingChatSettings: (orgId: string) => [...qk.bookingChats(orgId), 'settings'] as const,
  bookingThreadChat: (orgId: string) => [...qk.org(orgId), 'booking-thread-chat'] as const,
  bookingThreadChatOf: (orgId: string, threadId: string) =>
    [...qk.bookingThreadChat(orgId), 'state', { threadId }] as const,
  bookingThreadChatMessagesAll: (orgId: string, threadId: string) =>
    [...qk.bookingThreadChat(orgId), 'messages', threadId] as const,
  bookingThreadChatMessages: (orgId: string, threadId: string, search = '') =>
    [...qk.bookingThreadChatMessagesAll(orgId, threadId), { search }] as const,
  bookingThreadChatMentions: (orgId: string, threadId: string, q = '') =>
    [...qk.bookingThreadChat(orgId), 'mentions', { threadId, q }] as const,
  /* Byråets interne rom om en forespørsel (QA-runde 3 punkt 10). */
  internalChat: (orgId: string) => [...qk.org(orgId), 'internal-chat'] as const,
  internalChatOf: (orgId: string, enquiryId: string) =>
    [...qk.internalChat(orgId), 'state', { enquiryId }] as const,
  internalChatMessagesAll: (orgId: string, enquiryId: string) =>
    [...qk.internalChat(orgId), 'messages', enquiryId] as const,
  internalChatMessages: (orgId: string, enquiryId: string, search = '') =>
    [...qk.internalChatMessagesAll(orgId, enquiryId), { search }] as const,
  internalChatMentions: (orgId: string, enquiryId: string, q = '') =>
    [...qk.internalChat(orgId), 'mentions', { enquiryId, q }] as const,
  performanceChatOf: (orgId: string, performanceId: string) =>
    [...qk.performanceChat(orgId), 'state', { performanceId }] as const,
  /**
   * En side av samtalen. SØKETEKSTEN ER EN DEL AV NØKKELEN (sak 81.4).
   *
   * Hvert søk er sin egen bufrede liste, så å slette et tegn og skrive det igjen er umiddelbart —
   * og å tømme feltet henter fram hele samtalen slik den var, fordi den lå der hele tiden. Den
   * samme formen `performanceChatMentions` under bruker, av den samme grunnen.
   *
   * En invalidering på `qk.performanceChatMessages(org, id)` uten søketekst treffer bare den tomme
   * lista; skrivingene invaliderer derfor prefikset over — se `queries.ts`.
   */
  /**
   * EVERY search of ONE act's chat — the prefix a write invalidates.
   *
   * The act's id is its own ELEMENT rather than a field of the object at the end, and that is what
   * makes this work: TanStack matches a prefix, so a key ending in `{ search: 'SK41' }` is only
   * reachable from a prefix that stops before it. With the id inside the object there would be no
   * such prefix, and a message posted while somebody had a search open would leave their list
   * stale — silently, which is the way a cache bug always arrives.
   */
  performanceChatMessagesAll: (orgId: string, performanceId: string) =>
    [...qk.performanceChat(orgId), 'messages', performanceId] as const,
  performanceChatMessages: (orgId: string, performanceId: string, search = '') =>
    [...qk.performanceChatMessagesAll(orgId, performanceId), { search }] as const,
  /** Kandidatene for én @-omtale. Egen nøkkel per søketekst, så et treff er der igjen umiddelbart. */
  performanceChatMentions: (orgId: string, performanceId: string, q: string) =>
    [...qk.performanceChat(orgId), 'mentions', { performanceId, q }] as const,

  /**
   * RESSURSLAGET (trinn 1), og de fire nøklene under ett prefiks.
   *
   * ALT LIGGER UNDER `qk.resources(orgId)`, og det er formen som gjør invalideringen enkel: en
   * tildeling som skrives endrer BÅDE lista, konfliktene OG tellerne i avdelingstreet, fordi en
   * konflikt er et forhold mellom rader. En mutasjon som måtte huske tre prefikser ville før eller
   * siden huske to.
   *
   * Årgangen står som et eget ELEMENT og ikke som et felt i objektet til slutt, av grunnen
   * `performanceChatMessagesAll` gir: TanStack matcher prefiks, og en id inne i objektet kan ingen
   * prefiks stoppe før.
   */
  resources: (orgId: string) => [...qk.org(orgId), 'resources'] as const,
  resourceTypeList: (orgId: string, festivalId: string | null, includeArchived = false) =>
    [...qk.resources(orgId), 'types', { festivalId, includeArchived }] as const,
  resourceList: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.resources(orgId), 'list', editionId, filters] as const,
  allocationList: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.resources(orgId), 'allocations', editionId, filters] as const,
  allocationConflicts: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.resources(orgId), 'conflicts', editionId, filters] as const,
  resourceDepartmentSummary: (orgId: string, editionId: string) =>
    [...qk.resources(orgId), 'department-summary', editionId] as const,
  /**
   * Sjåførvelgeren (trinn 2.5). Under det SAMME prefikset selv om den svarer med mennesker.
   *
   * Den hører hjemme her fordi den er en del av bestillingsboksen, og fordi svaret endrer seg av
   * det samme som resten: et skift som flyttes gjør en sjåfør utilgjengelig. Under `qk.people`
   * ville den overlevd en tildeling som nettopp endret hvem som er ledig.
   */
  allocationDrivers: (orgId: string, editionId: string, window: Record<string, unknown>) =>
    [...qk.resources(orgId), 'drivers', editionId, window] as const,
  /**
   * «Liste til kjøkkenet» (trinn 4.3). Under det samme prefikset, av samme grunn sjåføren er det.
   *
   * Svaret følger BÅDE tildelingen og deltakelsene: flyttes noen av bussen, eller skriver noen
   * «vegetar» på et personkort, er summen en annen. `qk.resources` feies av det første; det andre
   * feies av `qk.participations` og tas her fordi lista hentes på nytt hver gang den åpnes.
   */
  allocationSummary: (orgId: string, allocationId: string) =>
    [...qk.resources(orgId), 'summary', allocationId] as const,
  /**
   * Tidslinja (trinn 5.1). Under ressursprefikset selv om halve svaret er skift.
   *
   * Den hører hjemme her fordi den feies av det samme: en tildeling som flyttes endrer den, og en
   * skiftendring gjør det også — derfor feier skiftskrivingene dette prefikset i tillegg til sitt
   * eget. Under `qk.crew` ville den overlevd en bil som nettopp ble booket.
   */
  allocationTimeline: (orgId: string, editionId: string, window: Record<string, unknown>) =>
    [...qk.resources(orgId), 'timeline', editionId, window] as const,
  /**
   * Hendelseslaget (C1, 0924). Under ressursprefikset av samme grunn som tidslinja: reservasjoner
   * og vakter feier det. Reiser, rom og middager feier det ikke — derfor hentes svaret på nytt hver
   * gang et panel åpnes (`staleTime: 0` i spørringen), framfor at tolv mutasjoner skal huske det.
   */
  editionEvents: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.resources(orgId), 'events', editionId, filters] as const,

  /**
   * DELTAKELSESLAGET (trinn 3), og de fire nøklene under ett prefiks.
   *
   * EGET PREFIKS OG IKKE UNDER `qk.crew(orgId)`, selv om de står på den samme siden. `qk.crew`
   * er vaktplanens og frivilliglistas, og de feies av hver eneste skiftendring — et skift som
   * flyttes ville da kastet hele Personer-lista og hentet 2 000 rader på nytt for data som ikke
   * beveget seg.
   *
   * Den andre veien holder ikke: en deltakelse som skrives feier BEGGE, fordi de fire listene står
   * ved siden av hverandre og handler om de samme menneskene. Se `participation-queries.ts`.
   */
  participations: (orgId: string) => [...qk.org(orgId), 'participations'] as const,
  /** Kortet om personen i huset (grep A1): standardfeltene med kilde. Under `person-card`, så alle kort friskes samlet. */
  personCards: (orgId: string) => [...qk.org(orgId), 'person-card'] as const,
  personCard: (orgId: string, personId: string) => [...qk.personCards(orgId), personId] as const,
  personFieldList: (orgId: string, festivalId: string | null, includeArchived = false) =>
    [...qk.participations(orgId), 'fields', { festivalId, includeArchived }] as const,
  /** Svarene en sletting av feltet tar med seg (A5). Under deltakelsene, så en skriving feier tallet. */
  personFieldUsage: (orgId: string, personFieldId: string) =>
    [...qk.participations(orgId), 'field-usage', personFieldId] as const,
  participationRoleList: (orgId: string, includeArchived = false) =>
    [...qk.participations(orgId), 'roles', { includeArchived }] as const,
  participationList: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.participations(orgId), 'list', editionId, filters] as const,
  participationDepartmentSummary: (orgId: string, editionId: string) =>
    [...qk.participations(orgId), 'department-summary', editionId] as const,
  /**
   * «Hvem er med?» (QA13 punkt 32): medlemmene i «Ny festival» ('new-festival'), «Ny årgang»
   * (festivalen) og «Legg til person» (årgangen). Under deltakelsesprefikset, så en deltakelse som
   * skrives — også fra «Legg til» — feier hvem som er med.
   */
  memberChoices: (
    orgId: string,
    scope: 'new-festival' | { festivalId: string } | { editionId: string },
  ) => [...qk.participations(orgId), 'member-choices', scope] as const,
  /* Personvelgeren (grep B1). Under deltakelsene: hver skriving der feier velgeren med. */
  editionPersonOptions: (orgId: string, editionId: string, filters: Record<string, unknown>) =>
    [...qk.participations(orgId), 'person-options', editionId, filters] as const,

  folders: (orgId: string) => [...qk.org(orgId), 'folders'] as const,
  folderList: (orgId: string, editionId: string | null) =>
    [...qk.folders(orgId), 'list', { editionId }] as const,
  /**
   * Hvor mye som står i én mappe, hele veien ned (QA 12, #15).
   *
   * UNDER `folders`-prefikset, som er hele poenget: tallet er rekursivt, så en fil lastet opp i en
   * undermappe gjør det gammelt — og hver skriving i utforskeren ugyldiggjør allerede hele
   * prefikset. En nøkkel ved siden av ville vært et tall som bare ble oppdatert av tilfeldigheter.
   */
  folderContents: (orgId: string, folderId: string) =>
    [...qk.folders(orgId), 'contents', folderId] as const,
  /* Sak 217.15: hvor mange programposter slettingen av en scene vil møte. Under `stages`. */
  stageUsage: (orgId: string, stageId: string) => [...qk.stages(orgId), 'usage', stageId] as const,
  /* QA13 256.8: «Scener» som en visning av ressursene. Under `stages`, så en ny scene feier den. */
  stagePlaces: (orgId: string, festivalId: string, editionId: string | null) =>
    [...qk.stages(orgId), 'places', { festivalId, editionId }] as const,
  /*
   * Spillesteder (agent B). Lista, salen og kalenderen under ÉTT prefiks: en forespørsel eller en
   * favoritt endrer både kortet og kalenderen, og invalideringen skal ikke måtte huske tre nøkler.
   */
  venueMarket: (orgId: string) => [...qk.org(orgId), 'venue-market'] as const,
  venueListing: (orgId: string, filters: Record<string, unknown>) =>
    [...qk.venueMarket(orgId), 'listing', filters] as const,
  venueRoom: (orgId: string, roomId: string) => [...qk.venueMarket(orgId), 'room', roomId] as const,
  venueRoomAvailability: (orgId: string, roomId: string, month: string) =>
    [...qk.venueMarket(orgId), 'room', roomId, 'availability', month] as const,
  venueMyRequests: (orgId: string) => [...qk.venueMarket(orgId), 'my-requests'] as const,
  /* Funn 27: én egen forespørsel, åpnet fra «Mine forespørsler» uansett moduler. */
  venueMyRequest: (orgId: string, requestId: string) =>
    [...qk.venueMarket(orgId), 'my-requests', requestId] as const,
  /* Plattformregisteret sett fra organisasjonen (QA15-S E): kortene leser det, så samme prefiks. */
  venueDirectoryRoom: (orgId: string, roomId: string) =>
    [...qk.venueMarket(orgId), 'directory', roomId] as const,
  /*
   * Spillestedet sett fra huset selv (agent A): salene, leien, synligheten og kveldene under ÉTT
   * prefiks. En kveld som lages endrer lista, Oversikt og kalenderen; en sal endrer alt.
   */
  venueHouse: (orgId: string) => [...qk.org(orgId), 'venue-house'] as const,
  venueHouseRooms: (orgId: string, festivalId: string) =>
    [...qk.venueHouse(orgId), 'rooms', festivalId] as const,
  venueHouseRoomRate: (orgId: string, roomId: string) =>
    [...qk.venueHouse(orgId), 'rate', roomId] as const,
  venueHouseNights: (orgId: string, festivalId: string, filters: Record<string, unknown>) =>
    [...qk.venueHouse(orgId), 'nights', festivalId, filters] as const,
  venueHouseNight: (orgId: string, nightId: string) =>
    [...qk.venueHouse(orgId), 'night', nightId] as const,
  venueHouseOverview: (orgId: string, festivalId: string) =>
    [...qk.venueHouse(orgId), 'overview', festivalId] as const,
  /* QA15-S (agent A): leiepakkene per sal, og husets kalender over kvelder og kø. */
  venueHouseRentPackages: (orgId: string, roomId: string) =>
    [...qk.venueHouse(orgId), 'rent-packages', roomId] as const,
  venueHouseCalendar: (orgId: string, filters: Record<string, unknown>) =>
    [...qk.venueHouse(orgId), 'calendar', filters] as const,
  /*
   * Husets mannskap og vaktplan (sak 229, V3) under huset: en vakt som bemannes gjennom
   * `useSetShiftAssignees` feier `qk.venueHouse`, og da planen og arrangementets vakter med den.
   */
  venueCrew: (orgId: string) => [...qk.venueHouse(orgId), 'crew'] as const,
  venueCrewList: (orgId: string, festivalId: string) =>
    [...qk.venueCrew(orgId), 'list', festivalId] as const,
  venueCrewPosts: (orgId: string, festivalId: string) =>
    [...qk.venueCrew(orgId), 'posts', festivalId] as const,
  /** Spillestedets poster som egen side (F39), med tellingene. */
  venuePosts: (orgId: string, festivalId: string) =>
    [...qk.venueCrew(orgId), 'manage-posts', festivalId] as const,
  venueCrewPlan: (orgId: string, festivalId: string, filters: Record<string, unknown>) =>
    [...qk.venueCrew(orgId), 'plan', festivalId, filters] as const,
  /** Salens maler for arrangement (QA15-S B6). Under huset: en brukt mal friskner kveldene. */
  venueTemplates: (orgId: string, roomId: string) =>
    [...qk.venueHouse(orgId), 'templates', roomId] as const,
  /* Har huset konserter (agent D sitt endepunkt)? Menyen spør; «Ny konsert» feier den. */
  concertPresence: (orgId: string) => [...qk.org(orgId), 'concert-presence'] as const,
  /*
   * Konsertens økonomi (agent E). Budsjett, avtaler og regionvarsler under ÉTT prefiks: en avtale
   * endrer budsjettet, og en ført leie endrer avtalefanen.
   */
  concertBudget: (orgId: string) => [...qk.org(orgId), 'concert-budget'] as const,
  concertBudgetDetail: (orgId: string, concertId: string) =>
    [...qk.concertBudget(orgId), 'budget', concertId] as const,
  concertAgreements: (orgId: string, concertId: string) =>
    [...qk.concertBudget(orgId), 'agreements', concertId] as const,
  concertRegionWarnings: (orgId: string, concertId: string) =>
    [...qk.concertBudget(orgId), 'region', concertId] as const,
  /*
   * Arrangørens konserter (agent D): lista, konserten og fanene under ÉTT prefiks. «Ny konsert»
   * og en statusendring endrer lista, oppsummeringen og siden på en gang.
   */
  orgConcerts: (orgId: string) => [...qk.org(orgId), 'org-concerts'] as const,
  orgConcertList: (orgId: string, filters: Record<string, unknown>) =>
    [...qk.orgConcerts(orgId), 'list', filters] as const,
  orgConcertStats: (orgId: string) => [...qk.orgConcerts(orgId), 'stats'] as const,
  orgConcert: (orgId: string, concertId: string) =>
    [...qk.orgConcerts(orgId), 'detail', concertId] as const,
  orgConcertPart: (orgId: string, concertId: string, part: string) =>
    [...qk.orgConcerts(orgId), 'detail', concertId, part] as const,
  concertTemplates: (orgId: string) => [...qk.org(orgId), 'concert-templates'] as const,
  /*
   * Produksjonsadvancen (agent F): arrangørens Advance-fane, husets kveld, byråets sal og byråets
   * egne konserter. ÉTT prefiks: et avvik huset skriver er det samme avviket arrangøren leser.
   */
  productionAdvance: (orgId: string) => [...qk.org(orgId), 'production-advance'] as const,
  concertAdvance: (orgId: string, concertId: string) =>
    [...qk.productionAdvance(orgId), 'concert', concertId] as const,
  nightProduction: (orgId: string, nightId: string) =>
    [...qk.productionAdvance(orgId), 'night', nightId] as const,
  performanceRoom: (orgId: string, performanceId: string) =>
    [...qk.productionAdvance(orgId), 'room', performanceId] as const,
  performanceConcertHead: (orgId: string, performanceId: string) =>
    [...qk.productionAdvance(orgId), 'head', performanceId] as const,
  ownConcerts: (orgId: string, artistId: string) =>
    [...qk.productionAdvance(orgId), 'own', artistId] as const,

  /*
   * DATOFORESPØRSELEN (agent C). Arrangørens og husets side har hvert sitt prefiks: et hus som også
   * arrangerer har begge, og et svar feier begge (svaret endrer kopien arrangøren leser, og
   * konserten). Konsertene feies også — status og dato følger forespørselen.
   */
  dateRequests: (orgId: string) => [...qk.org(orgId), 'date-requests'] as const,
  dateRequestList: (orgId: string, concertId: string) =>
    [...qk.dateRequests(orgId), 'list', concertId] as const,
  dateRequestMessages: (orgId: string, requestId: string) =>
    [...qk.dateRequests(orgId), 'messages', requestId] as const,
  dateRequestArtistOptions: (orgId: string) =>
    [...qk.dateRequests(orgId), 'artist-options'] as const,
  venueEnquiries: (orgId: string) => [...qk.org(orgId), 'venue-enquiries'] as const,
  venueEnquiryList: (orgId: string, filters: Record<string, unknown>) =>
    [...qk.venueEnquiries(orgId), 'list', filters] as const,
  venueEnquirySummary: (orgId: string) => [...qk.venueEnquiries(orgId), 'summary'] as const,
  venueEnquiry: (orgId: string, enquiryId: string) =>
    [...qk.venueEnquiries(orgId), 'detail', enquiryId] as const,
  venueEnquiryMessages: (orgId: string, enquiryId: string) =>
    [...qk.venueEnquiries(orgId), 'messages', enquiryId] as const,
  /*
   * SPILLESTEDETS «SISTE AKTIVITET I HUSET» (sak 229, V1): endringsloggen for et utvalg tabeller.
   * Under `qk.activity`, så det som feier aktiviteten feier denne også.
   */
  typedActivityList: (orgId: string, entityTypes: readonly string[], limit: number) =>
    [...qk.activity(orgId), 'types', { entityTypes: [...entityTypes], limit }] as const,
  /*
   * DOKUMENTMAPPA PÅ SPILLESTEDETS ARRANGEMENT (sak 229, V4). Under arrangementets egen nøkkel, så
   * det som feier arrangementet feier mappa også.
   */
  venueNightDocuments: (orgId: string, nightId: string) =>
    [...qk.venueHouseNight(orgId, nightId), 'documents'] as const,
  /*
   * AKKREDITERING PÅ ARRANGEMENTET (sak 229.3, V5). Huset og arrangøren leser de samme radene fra
   * hver sin side; ÉTT prefiks, så en skriving fra den ene siden feier den andre også.
   */
  nightAccreditations: (orgId: string) => [...qk.org(orgId), 'night-accreditations'] as const,
  nightAccreditationList: (orgId: string, nightId: string) =>
    [...qk.nightAccreditations(orgId), 'night', nightId] as const,
  concertNightAccreditationList: (orgId: string, concertId: string) =>
    [...qk.nightAccreditations(orgId), 'concert', concertId] as const,
  /*
   * Grunnoppsett og ressurser på spillestedet (V2, 0447). Under `venueHouse`, så en flyttet eller
   * avlyst kveld (som feier huset) også feier reservasjonene og konfliktene.
   */
  venueHouseBaseResources: (orgId: string, roomId: string) =>
    [...qk.venueHouse(orgId), 'base-resources', roomId] as const,
  venueHouseNightResources: (orgId: string, nightId: string) =>
    [...qk.venueHouse(orgId), 'night-resources', nightId] as const,
  venueHouseResources: (orgId: string, festivalId: string, filters: Record<string, unknown>) =>
    [...qk.venueHouse(orgId), 'resources', festivalId, filters] as const,
  /*
   * LENKESIDEN (sak 232, P6). Global av samme grunn som `invitationPreview`: den som åpner lenken
   * har verken sesjon eller organisasjon, og tokenet er hele spørsmålet.
   */
  shareLink: (token: string) => [...qk.global(), 'share-link', token] as const,
  /* Lenkepersonene på årgangen, under personlista, så det som feier lista feier ikonet også. */
  linkPeople: (orgId: string, editionId: string) =>
    [...qk.participations(orgId), 'link-people', editionId] as const,
  /*
   * GJESTENS DELINGER I HUSET (sak 232, P5): under huset, fordi svaret gjelder huset headeren navngir
   * — og det er nettopp det gjestemodusen bytter. Nettleserens `can()` leser den (`auth/actor.ts`).
   */
  guestScope: (orgId: string) => [...qk.org(orgId), 'guest-scope'] as const,
  /*
   * `/v1/me` MED ET ANNET HUS I HEADEREN (P11): rollen og cellene personen har der. Klokka trenger
   * hjemmets i gjestemodus, der `qk.me()` er gjesteaktørens. Under huset, ikke globalt.
   */
  meIn: (orgId: string) => [...qk.org(orgId), 'me'] as const,
  /** «Mine eksporter» (docs/EKSPORT.md §8): leserens egne jobber i huset, de siste 7 dagene. */
  exports: (orgId: string) => [...qk.org(orgId), 'exports'] as const,
  exportList: (orgId: string) => [...qk.exports(orgId), 'list'] as const,
  /*
   * AVTALEARKET (C3, sak 274): ett delt objekt per sak. Under huset; et motbud eller et godta feier
   * hele prefikset, og budene, innboksen og samtalen feies av mutasjonen selv.
   */
  dealSheets: (orgId: string) => [...qk.org(orgId), 'deal-sheets'] as const,
  dealSheet: (orgId: string, id: string) => [...qk.dealSheets(orgId), 'one', id] as const,
  dealSheetLookup: (orgId: string, lookup: Readonly<Record<string, string>>) =>
    [...qk.dealSheets(orgId), 'lookup', lookup] as const,
  /* G2: leiearkene bak konsertens datoforespørsler, i ett kall. */
  dealSheetsForDateRequests: (orgId: string, dateRequestIds: readonly string[]) =>
    [...qk.dealSheets(orgId), 'for-date-requests', [...dateRequestIds].sort()] as const,
  /* Vilkårslista i Innstillinger, og budmodalens valg av standardvilkår. */
  dealStandardTerms: (orgId: string) => [...qk.org(orgId), 'deal-standard-terms'] as const,
  /*
   * DEN FELLES KONTRAKTEN (C4–C8, sak 274). Én prefiks: en signatur, et tillegg eller en
   * registrering feier kontrakten, oppslaget, brikkene og loggen sammen.
   */
  contracts: (orgId: string) => [...qk.org(orgId), 'contracts'] as const,
  contract: (orgId: string, id: string) => [...qk.contracts(orgId), 'one', id] as const,
  contractLookup: (orgId: string, lookup: Readonly<Record<string, string>>) =>
    [...qk.contracts(orgId), 'lookup', lookup] as const,
  contractEvents: (orgId: string, id: string) => [...qk.contracts(orgId), 'events', id] as const,
  contractDelegates: (orgId: string, id: string) =>
    [...qk.contracts(orgId), 'delegates', id] as const,
  /** Siden kontrakten bor på for huset som leser (varselet på bjella, F2). */
  contractPlace: (orgId: string, id: string) => [...qk.contracts(orgId), 'place', id] as const,
  contractChips: (orgId: string, params: Readonly<Record<string, string>>) =>
    [...qk.contracts(orgId), 'chips', params] as const,
  contractSettings: (orgId: string) => [...qk.org(orgId), 'contract-settings'] as const,
  signingAuthorities: (orgId: string) => [...qk.org(orgId), 'signing-authorities'] as const,
  /* Signeringssiden uten innlogging: ingen organisasjon, tokenet er hele spørsmålet. */
  publicSigning: (token: string) => [...qk.global(), 'contract-signing', token] as const,
  /* Engangskodene i Control Room (fake-driveren, 274.5 a). */
  contractCodes: () => [...qk.global(), 'contract-codes'] as const,
  /*
   * UTKASTENE (B-E, D9): brukerens egne, per samtale eller per artist. Én prefiks, så «Forkast» og
   * «Send» feier alle listene som kan ha vist utkastet.
   */
  drafts: (orgId: string) => [...qk.org(orgId), 'drafts'] as const,
  draftsFor: (orgId: string, targetType: string, targetId: string) =>
    [...qk.drafts(orgId), 'for', targetType, targetId] as const,
  /* 1090: de automatiske e-postene til frivillige, per årgang. Under frivillige, så de feies med dem. */
  volunteerMessages: (orgId: string, editionId: string) =>
    [...qk.volunteers(orgId), 'messages', editionId] as const,
  /* Persontypene G (§2.1, §2.6): artisttilgangen og Personer-fanen. Under registeret, så rosteren feier med. */
  artistAccess: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'access', artistId] as const,
  artistPeople: (orgId: string, artistId: string) =>
    [...qk.bookingRegister(orgId), 'people', artistId] as const,
  /* Mottakerens artister og Personer (§2.6) — under delingene, så et svar på en deling feier med. */
  myArtists: () => [...qk.myShares(), 'my-artists'] as const,
  myArtistPeople: (artistId: string) =>
    [...qk.myShares(), 'my-artists', artistId, 'people'] as const,
  /* J (1131): den delte artistsiden lest etter tilgangen på artisten — under delingene, som Personer. */
  myArtistView: (artistId: string) => [...qk.myShares(), 'my-artists', artistId, 'view'] as const,
} satisfies Record<string, unknown>;
